import {
    initializeSuiSignalR,
    disconnectSuiSignalR,
    sendSuiGameAction,
    waitForSuiServerResponse,
    onSuiMessage,
    onSuiConnection,
    onSuiError,
    onSuiGameState,
    onSuiScoreUpdate,
    onSuiPlayerAction,
    suiSignalRManager
} from '../../api/gameFiSignalr';

// 【方塊類型常數】- 與後端 TileAttributeType enum 對應
const TILE_TYPES = {
    NORMAL: 0,      // Normal - 普通方塊
    BOMB: 1,        // Bomb - 炸彈方塊
    RANDOM: 2,      // Random - 隨機方塊
    SKULL: 3,       // Skull - 骷髏方塊
    TELEPORT: 4     // Teleport - 傳送方塊
};

// 【Tile 類別】- 代表棋盤上的單個方塊
class Tile {
    /**
     * 創建一個方塊
     * @param {number} value - 方塊數值 (0 = 空格)
     * @param {number} type - 方塊類型 (使用 TILE_TYPES 常數)
     * @param {number} randomDisplay - 隨機方塊顯示數字（僅用於動畫）
     */
    constructor(value = 0, type = TILE_TYPES.NORMAL, randomDisplay = 0) {
        this.value = value;           // 數值 (0 = 空格)
        this.type = type;             // 方塊類型
        this.randomDisplay = randomDisplay;       // 隨機方塊顯示數字（僅用於動畫）
    }

    // 類型檢查方法
    isBomb() { return this.type === TILE_TYPES.BOMB; }
    isRandom() { return this.type === TILE_TYPES.RANDOM; }
    isSkull() { return this.type === TILE_TYPES.SKULL; }
    isTeleport() { return this.type === TILE_TYPES.TELEPORT; }
    isNormal() { return this.type === TILE_TYPES.NORMAL; }
    isEmpty() { return this.value === 0; }

    /**
     * 檢查是否為金色方塊（高價值方塊，不能被炸彈炸毀）
     * @returns {boolean} 當方塊數值 >= 128 時返回 true
     */
    isGold() { return this.value >= 128; }

    /**
     * 複製方塊（用於撤銷功能）
     * @returns {Tile} 新的方塊副本
     */
    clone() {
        const newTile = new Tile(this.value, this.type);
        newTile.randomDisplay = this.randomDisplay;
        return newTile;
    }
}


export default class Game20Engine {
    // 【狀態機定義】- 遊戲狀態常數
    static STATES = {
        IDLE: '1',                    // 空閒狀態：等待用戶輸入
        MOVING: '2',                // 移動狀態：方塊正在移動和合併
        PROCESSING: '3',        // 處理狀態：檢查合併結果和爆炸條件
        EXPLODING: '4',          // 爆炸狀態：炸彈爆炸動畫進行中
        ADDING_TILE: '5',      // 添加方塊狀態：生成新方塊
        GAME_OVER: '6',          // 遊戲結束狀態
        PAUSED: '7'                 // 暫停狀態
    };

    constructor() {

        this.VALUE_MULTIPLIER = 1000;
        this.MAX_VALUE = 2048;

        this.suiSignalRManager = suiSignalRManager; // SignalR 連線管理器
        this.signalRConnection = null; // SignalR 連線實例
        this.signalRCleanupFunctions = []; // 存儲 SignalR 監聽器清理函數

        // 【棋盤狀態】- 使用 Tile 物件陣列統一管理方塊資訊
        this.board = [];                // 主棋盤：5x5 的 Tile 物件陣列

        // 【向後相容陣列】- 保留舊的陣列結構以支援現有程式碼
        this.bombBoard = [];            // 炸彈標記：記錄哪些位置是炸彈方塊
        this.randomBoard = [];          // 隨機方塊標記：記錄哪些位置是待確認的隨機方塊
        this.randomDisplayBoard = [];   // 隨機方塊顯示緩衝：儲存隨機方塊的動畫數字
        this.skullBoard = [];           // 骷髏頭方塊標記陣列
        this.teleportBoard = [];        // 傳送方塊標記：記錄哪些位置是傳送方塊

        // ✅ ETH版本：支援無地址的匿名遊戲
        this.address = sessionStorage.getItem('zklogin_user_address');

        // ✅ 如果沒有地址，生成臨時會話ID用於SignalR連接
        if (!this.address) {
            this.sessionId = sessionStorage.getItem('anonymous_session_id');
            if (!this.sessionId) {
                this.sessionId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                sessionStorage.setItem('anonymous_session_id', this.sessionId);
                console.log('🎮 已生成匿名會話ID:', this.sessionId);
            }
        }

        // 【遊戲統計】
        this.score = 0;                 // 當前得分
        this.moves = 0;                 // 移動次數
        this.bestScore = parseInt(localStorage.getItem('game20BestScore') || '0'); // 最高分

        // 【撤銷功能狀態】
        this.previousState = null;
        this.previousBoard = null;      // 儲存整個 Tile 物件陣列的副本

        // 【遊戲狀態】
        this.hasWon = false;            // 勝利狀態：是否已達成20

        // 【方塊生成機率設定】
        // 炸彈方塊機率：數值2(3%), 數值4(2%), 數值8(1%) = 總計6%
        this.bombProbabilities = {
            2: 0.03,  // 數值2的炸彈：3%機率
            4: 0.02,  // 數值4的炸彈：2%機率  
            8: 0.01   // 數值8的炸彈：1%機率
        };

        // 傳送方塊機率：數值2(2%), 數值4(1.5%), 數值8(1%) = 總計4.5%
        this.teleportProbabilities = {
            2: 0.02,  // 數值2的傳送：2%機率
            4: 0.015, // 數值4的傳送：1.5%機率
            8: 0.01   // 數值8的傳送：1%機率
        };

        // 【隨機方塊系統設定】
        this.randomTileProbability = 0.05;        // 隨機方塊生成機率：5%
        this.randomAnimationInterval = null;      // 隨機數字變換動畫的定時器
        this.randomNumbers = [1, 2, 4, 8, 16];    // 隨機方塊確認時的可能數值

        // 【骷髏頭方塊系統設定】
        this.heartTileProbability = 0.01;         // 骷髏頭方塊生成機率：1%

        // 【爆炸系統設定】
        this.explosionMode = 'cross';   // 爆炸模式：'cross'=十字形, 'square'=方形
        this.explosionRange = 1;        // 爆炸範圍：影響周圍1格的距離

        // 【動作控制】- 防止動畫期間的重複操作
        this.isActionLocked = false;    // 動作鎖定：防止爆炸/移動期間的誤觸
        this.isExploding = false;       // 爆炸狀態：標記是否正在執行爆炸動畫

        // 【狀態機系統】- 遊戲流程控制
        this.gameState = Game20Engine.STATES.IDLE;  // 當前遊戲狀態
        this.stateHistory = [];                     // 狀態歷史記錄
        this.stateStartTime = 0;                    // 當前狀態開始時間
        this.isStateComplete = true;                // 當前狀態是否已完成
        this.stateTransitionLocked = false;         // 狀態轉換鎖定
        this.stateTimeouts = [];                    // 狀態相關的計時器

        // 【速度配置】- 用於統一調整遊戲節奏 (手動調整這裡即可改變全域速度)
        this.speedConfig = {
            moveDuration: 250,         // 方塊移動動畫時長 (ms)
            moveDebounce: 50,         // 移動防抖延遲 (ms)
            transitionDelay: 150,     // 基礎狀態切換延遲 (ms)
            addingTileDelay: 1,       // 添加方塊完成後的延遲 (ms)
            explosionDelay: 200,      // 爆炸特效後清除方塊的延遲 (ms)
            explosionShake: 300,      // 螢幕震動持續時間 (ms)
            explosionFinish: 400      // 爆炸整體流程完成延遲 (ms)
        };

        // 【防抖控制】- 防止重複觸發移動
        this.lastMoveTime = 0;          // 上次移動的時間戳
        this.moveDebounceDelay = this.speedConfig.moveDebounce; // 防抖延遲（毫秒）
        this.isMoving = false;          // 移動狀態鎖定

        // 【心跳檢測】- SignalR 連線保活
        this.heartbeatInterval = null;  // 心跳檢測定時器
        this.heartbeatIntervalMs = 30000; // 心跳間隔 30 秒

        // 【SignalR 排程】- 定期執行 getNotSignEvents
        this.notSignEventsInterval = null;  // getNotSignEvents 排程定時器
        this.notSignEventsIntervalMs = 10 * 1000; // 排程間隔 10 秒

        // 【Canvas 渲染系統】
        this.canvas = null;             // Canvas 元素
        this.ctx = null;                // Canvas 2D 上下文
        this.cellSize = 0;              // 每個格子的大小
        this.cellPadding = 10;          // 格子之間的間距
        this.tileAnimations = [];       // 方塊動畫隊列
        this.animationId = null;        // requestAnimationFrame ID
        this.particleEffects = [];      // 粒子效果隊列
        this.tilePositions = {};        // 追蹤每個方塊的當前位置（用於移動動畫）
        this.previousBoardState = null; // 上一幀的棋盤狀態

        // 【遊戲初始化】
        this.initBoard();               // 初始化5x5棋盤陣列
        this.initCanvas();              // 初始化 Canvas 畫布
        this.setupEventListeners();     // 設定鍵盤和觸控事件
        this.updateDisplay();           // 更新分數顯示
        this.startAnimationLoop();      // 啟動動畫循環

        // ✅ ETH版本：檢查是否有保存的 gameId
        // 如果有 gameId，說明是頁面刷新，不應該創建新遊戲，而是等待恢復狀態
        // 如果沒有 gameId，才創建新遊戲
        const savedGameId = sessionStorage.getItem('gameId');
        if (!savedGameId) {
            // 沒有保存的 gameId，創建新遊戲
            this.initGameWithSignalR();
        } else {
            // 有保存的 gameId，只初始化 SignalR 連接，不創建新遊戲
            // 遊戲狀態會在 restoreGameStateFromServer 中恢復
            // 只初始化 SignalR 連接，不創建新遊戲
            this.initSignalRConnection().catch(error => {
                console.error('❌ SignalR 連接初始化失敗:', error);
            });
        }
    }

    /**
     * 【有登入的初始化流程】- 先連線SignalR，再添加方塊，最後渲染
     * @returns {Promise<{gameId: string, multiplayerGameId: string}|null>} 返回遊戲創建結果
     */
    async initGameWithSignalR(gameType = 0, multiplayerGameId = '', userSignalRConnection = '') {
        let responseOut;
        try {
            // 顯示連線中的 modal
            this.showConnectingModal();

            // 1. 先建立 SignalR 連線
            await this.initSignalRConnection();

            // 2. 等待連線成功
            if (this.signalRConnection && this.isConnected) {

                // 更新 modal 狀態為連線成功
                this.updateConnectingModal('連線成功！正在初始化遊戲...');

                // ✅ 獲取現有的多人遊戲房間ID（如果有的話）
                let response = await this.sendPlayerActionSignalR(
                    {
                        type: 'new_game',
                        gameType: gameType,
                        multiplayerGameId: multiplayerGameId,
                        userSignalRConnection: userSignalRConnection || '' // ✅ 確保不傳遞 null 值
                    });

                // ✅ 檢查回應是否為 null 或 undefined
                if (!response) {
                    console.error('❌ 服務器回應為 null 或 undefined');
                    throw new Error('服務器回應為空，無法初始化遊戲');
                }

                // ✅ 檢查回應類型是否為錯誤
                if (response.type === 'error') {
                    console.error('❌ 服務器返回錯誤:', response.message);
                    throw new Error(response.message || '服務器返回錯誤');
                }

                // ✅ 檢查 gameId 是否存在
                if (!response.gameId) {
                    console.error('❌ 服務器返回的 gameId 為空');
                    throw new Error('服務器返回的 gameId 為空，無法初始化遊戲');
                }

                // ✅ 保存遊戲 ID 和多人遊戲房間 ID
                sessionStorage.setItem('gameId', response.gameId);

                // ✅ 檢查 multiplayerGameId 是否有效
                if (response.multiplayerGameId && response.multiplayerGameId !== '') {
                    sessionStorage.setItem('multiplayer_game_id', response.multiplayerGameId);
                    console.log('✅ 已保存多人遊戲房間ID:', response.multiplayerGameId);
                } else {
                    console.warn('⚠️ 服務器返回的 multiplayerGameId 無效:', response.multiplayerGameId);
                }

                responseOut = response;

                // 3. 添加兩個初始方塊
                await this.addRandomTileSignalR();
                await this.addRandomTileSignalR();

                // 4. 最後渲染棋盤
                this.renderBoard();

                // 5. 重置狀態機，確保可以開始操作
                this.resetStateMachine();

                // 6. 確保觸控事件已設置（遊戲頁面可能剛剛載入）
                this.setupTouchControls();

                // 隱藏連線 modal
                this.hideConnectingModal();

            } else {
                console.warn('⚠️ SignalR連線失敗，使用本地邏輯作為備用');
                this.hideConnectingModal();
                this.initGameLocally();
                return null;
            }

        } catch (error) {
            console.error('❌ SignalR初始化失敗，使用本地邏輯作為備用:', error);
            this.hideConnectingModal();
            this.initGameLocally();
            return null;
        }

        // ✅ 返回遊戲創建結果
        return responseOut;
    }

    /**
     * 【顯示連線中的 Modal】
     */
    showConnectingModal() {
        const modal = document.getElementById('connectingModal');
        if (modal) {
            modal.style.display = 'flex';
            // 重置狀態
            const statusText = modal.querySelector('.connecting-status');
            if (statusText) {
                statusText.textContent = '正在連線中...';
            }
        }
    }

    /**
     * 【更新連線 Modal 狀態】
     */
    updateConnectingModal(message) {
        const modal = document.getElementById('connectingModal');
        if (modal) {
            const statusText = modal.querySelector('.connecting-status');
            if (statusText) {
                statusText.textContent = message;
            }
        }
    }

    /**
     * 【隱藏連線 Modal】
     */
    hideConnectingModal() {
        const modal = document.getElementById('connectingModal');
        if (modal) {
            modal.style.display = 'none';
        }
    }

    async initSignalRConnection() {
        // ✅ ETH版本：允許匿名連接
        if (!this.suiSignalRManager) {
            console.warn('⚠️ SignalR Manager 不可用');
            return;
        }

        try {
            // 初始化 SignalR 連線到遊戲伺服器
            const gameId = sessionStorage.getItem('gameId');
            // ✅ 使用地址或會話ID作為識別
            const identifier = this.address || this.sessionId;

            const success = await initializeSuiSignalR(identifier, gameId);

            if (success) {
                this.isConnected = true;
                this.signalRConnection = this.suiSignalRManager;

                // 註冊 SignalR 連線事件
                this.setupSignalREventListeners();

                // 連線成功後啟動心跳檢測和 getNotSignEvents 排程
                this.startHeartbeat();
                this.startNotSignEventsScheduler();
            } else {
                throw new Error('SignalR 連線初始化失敗');
            }

        } catch (error) {
            console.error('❌ 建立 SignalR 連線失敗:', error);
            this.handleConnectionError(error);
        }
    }

    // 設置 SignalR 事件監聽器
    setupSignalREventListeners() {
        // 先清理舊的監聽器，避免重複註冊
        this.cleanupSignalRListeners();

        // 連線狀態事件
        const cleanupConnection = onSuiConnection((status, data) => {
            switch (status) {
                case 'connected':
                    console.log('🎮 遊戲 SignalR 連線已建立');
                    this.isConnected = true;
                    this.startHeartbeat();
                    break;
                case 'disconnected':
                    console.log('🔌 SignalR 連線已斷開', data);
                    this.isConnected = false;
                    this.handleDisconnection(data);
                    break;
                case 'reconnecting':
                    console.log('🔄 正在嘗試重連...');
                    this.updateConnectingModal('連線中斷，正在重連...');
                    break;
                case 'reconnected':
                    console.log('✅ SignalR 重連成功');
                    this.isConnected = true;
                    this.updateConnectingModal('重連成功！正在同步遊戲狀態...');
                    this.startHeartbeat();
                    this.startNotSignEventsScheduler();
                    break;
            }
        });
        this.signalRCleanupFunctions.push(cleanupConnection);

        // 遊戲消息事件
        const cleanupMessage = onSuiMessage((message) => {
            this.handleServerData(message);
        });
        this.signalRCleanupFunctions.push(cleanupMessage);

        // 遊戲狀態更新事件
        const cleanupGameState = onSuiGameState((gameState) => {
            this.syncGameStateFromServer(gameState);
        });
        this.signalRCleanupFunctions.push(cleanupGameState);

        // 分數更新事件
        const cleanupScore = onSuiScoreUpdate((scoreData) => {
            this.updateScoreFromServer(scoreData.score);
        });
        this.signalRCleanupFunctions.push(cleanupScore);

        // 玩家動作事件
        const cleanupPlayerAction = onSuiPlayerAction((action) => {
            this.handlePlayerAction(action);
        });
        this.signalRCleanupFunctions.push(cleanupPlayerAction);

        // 錯誤事件
        const cleanupError = onSuiError((message, error) => {
            this.isConnected = false;
            this.handleConnectionError(error);
        });
        this.signalRCleanupFunctions.push(cleanupError);
    }

    // 清理 SignalR 監聽器
    cleanupSignalRListeners() {
        if (this.signalRCleanupFunctions && this.signalRCleanupFunctions.length > 0) {
            this.signalRCleanupFunctions.forEach(cleanup => {
                if (typeof cleanup === 'function') {
                    cleanup();
                }
            });
            this.signalRCleanupFunctions = [];
        }
    }

    // 處理伺服器資料
    handleServerData(data) {
        try {
            // SignalR 傳遞的是物件，不需要 JSON.parse
            const serverMessage = data;

            switch (serverMessage.type) {
                case 'score_update':
                    this.updateScoreFromServer(serverMessage.score);
                    break;
                case 'game_state_sync':
                    console.log('📡 收到遊戲伺服器狀態同步: game_state_sync');
                    this.syncGameStateFromServer(serverMessage.gameState);
                    break;
                case 'player_action':
                    this.handlePlayerAction(serverMessage.action);
                    break;
                case 'new_game_response':
                    break;
                case 'add_random_tile_response':
                    break;
                case 'move_response':
                    break;
                case 'confirm_random_tile_response':
                    break;
                case 'teleport_notification':
                    // 使用 insertOpponentTiles 方法添加傳送來的方塊
                    if (serverMessage.data && Array.isArray(serverMessage.data)) {
                        // 將 TeleportTile 格式轉換為 insertOpponentTiles 期望的格式
                        const opponentTiles = serverMessage.data.map(teleportTile => {
                            // 根據後端 TileType 數字值來判斷方塊類型
                            const tileType = teleportTile.TileType;
                            const value = teleportTile.Value;

                            return {
                                tile: teleportTile.Tile,
                                value: value,
                                tileType: tileType,
                                isBomb: tileType === 1, // Bomb = 1
                                isRandom: tileType === 2, // Random = 2
                                isHeart: tileType === 3, // Heart = 3
                                isTeleport: tileType === 4, // Teleport = 4
                                isNormal: tileType === 0 // Normal = 0
                            };
                        });

                        this.insertOpponentTiles(opponentTiles);
                    }
                    break;
                case 'opponent_game_over':

                    // 觸發對手遊戲結束事件
                    if (this.onOpponentGameOver && serverMessage.data) {
                        const gameOverData = {
                            opponentGameId: serverMessage.data.opponentGameId,
                            multiplayerGameId: serverMessage.data.multiplayerGameId,
                            opponentScore: serverMessage.data.opponentScore,
                            opponentIsWin: serverMessage.data.opponentIsWin,
                            timestamp: serverMessage.data.timestamp
                        };
                        console.log('🏁 [Engine] 準備觸發回調，數據:', gameOverData);
                        this.onOpponentGameOver(gameOverData);
                        console.log('✅ [Engine] 已觸發 onOpponentGameOver 回調');
                    } else {
                        console.warn('⚠️ [Engine] 無法觸發回調 - onOpponentGameOver:', !!this.onOpponentGameOver, 'data:', !!serverMessage.data);
                    }
                    break;
                default:
                    console.log('📡 未知的伺服器訊息類型:', serverMessage.type);
            }
        } catch (error) {
            console.error('❌ 解析伺服器資料失敗:', error);
        }
    }

    // 從伺服器更新分數
    updateScoreFromServer(serverScore) {
        // ✅ ETH版本：不需要address檢查
        if (serverScore !== undefined && serverScore !== null) {
            this.score = serverScore;
            this.updateDisplay();
        }
    }

    // 從伺服器同步遊戲狀態
    syncGameStateFromServer(serverGameState) {
        // ✅ ETH版本：不需要address檢查
        if (serverGameState) {
            // 根據伺服器資料更新本地遊戲狀態
            if (serverGameState.score !== undefined) {
                this.score = serverGameState.score;
            }
            if (serverGameState.board) {
                // ✅ 修正：伺服器返回的是 JSON 物件，不是 Tile 類別實例
                // 需要將所有資料轉換為 Tile 物件
                if (serverGameState.board[0] && serverGameState.board[0][0] && serverGameState.board[0][0].value !== undefined) {
                    // 有 value 屬性，但需要轉換為 Tile 實例
                    for (let i = 0; i < 5; i++) {
                        for (let j = 0; j < 5; j++) {
                            const tileData = serverGameState.board[i][j];
                            const value = tileData.value || 0;
                            const type = tileData.type !== undefined ? tileData.type : TILE_TYPES.NORMAL;
                            const randomDisplay = tileData.randomDisplay || 0;

                            // 創建新的 Tile 實例
                            this.board[i][j] = new Tile(value, type, randomDisplay);
                        }
                    }
                    this.syncLegacyArrays(); // 同步到舊陣列
                } else {
                    // 舊格式：數字陣列，需要轉換
                    for (let i = 0; i < 5; i++) {
                        for (let j = 0; j < 5; j++) {
                            const value = serverGameState.board[i]?.[j] || 0;
                            this.board[i][j] = new Tile(value, TILE_TYPES.NORMAL);
                        }
                    }
                    this.syncLegacyArrays();
                }
                this.renderBoard(); // 重新渲染棋盤
            }
        }
    }

    // 處理玩家動作
    handlePlayerAction(action) {
        console.log('🎮 收到玩家動作:', action);
        // 這裡可以處理來自伺服器的玩家動作
    }

    async sendPlayerActionSignalR(action) {
        // 檢查連線狀態 - 修正邏輯：優先檢查SignalR物件是否存在且可用
        if (!this.signalRConnection) {
            console.warn('⚠️ SignalR 連線物件不存在，嘗試重新連線...');
            this.updateConnectingModal('連線中斷，正在重新連線...');

            // 嘗試重新連線
            setTimeout(async () => {
                try {
                    await this.initSignalRConnection();
                    // 重連成功後重試發送
                    if (this.signalRConnection) {
                        return await this.sendPlayerActionSignalR(action);
                    }
                } catch (error) {
                    console.error('❌ 重連失敗，無法發送動作:', error);
                    this.updateConnectingModal('重連失敗，切換到本地模式...');
                    setTimeout(() => {
                        this.hideConnectingModal();
                    }, 2000);
                }
            }, 2000);
            return null;
        }

        // 檢查SignalR連線的實際狀態
        if (!this.isConnected) {
            console.warn('⚠️ SignalR 連線狀態異常，嘗試重新連線...');
            this.updateConnectingModal('連線中斷，正在重新連線...');

            // 嘗試重新連線
            setTimeout(async () => {
                try {
                    await this.initSignalRConnection();
                    // 重連成功後重試發送
                    if (this.signalRConnection && this.isConnected) {
                        return await this.sendPlayerActionSignalR(action);
                    }
                } catch (error) {
                    console.error('❌ 重連失敗，無法發送動作:', error);
                    this.updateConnectingModal('重連失敗，切換到本地模式...');
                    setTimeout(() => {
                        this.hideConnectingModal();
                    }, 2000);
                }
            }, 2000);
            return null;
        }

        // ✅ ETH版本：只檢查連接，不檢查address
        if (this.signalRConnection) {
            try {
                // console.log('📤 準備發送 SignalR 消息:', action);
                const result = await sendSuiGameAction(action);
                // console.log('✅ SignalR 消息發送成功，收到回應:', result);
                return result;
            } catch (error) {
                console.error('❌ 發送動作失敗:', error);
                this.handleConnectionError(error);
                return null;
            }
        } else {
            console.warn('⚠️ SignalR 連線不可用:', {
                hasConnection: !!this.signalRConnection,
                isConnected: this.isConnected
            });
            return null;
        }
    }

    // 等待服務器回應的方法
    async waitForServerResponse(actionType, timeout = 30000) { // 增加超時時間到 30 秒

        if (!this.signalRConnection) {
            console.error('❌ SignalR 未連接');
            throw new Error('SignalR 未連接');
        }

        try {
            const response = await waitForSuiServerResponse(actionType, timeout);
            console.log(`✅ 收到服務器回應: ${actionType}`, response);
            return response;
        } catch (error) {
            console.error(`❌ 等待服務器回應失敗: ${actionType}`, error);

            // 檢查是否是超時錯誤
            if (error.message.includes('Server timeout elapsed without receiving a message from the server')) {
                console.warn('⚠️ 服務器響應超時，嘗試重新連接...');
                this.updateConnectingModal('服務器響應超時，正在重新連接...');

                // 嘗試重新連接
                setTimeout(async () => {
                    try {
                        await this.initSignalRConnection();
                        this.updateConnectingModal('重新連接成功');
                        setTimeout(() => {
                            this.hideConnectingModal();
                        }, 2000);
                    } catch (reconnectError) {
                        console.error('❌ 重連失敗，無法發送動作:', reconnectError);
                        this.updateConnectingModal('重連失敗，切換到本地模式...');
                        setTimeout(() => {
                            this.hideConnectingModal();
                        }, 2000);
                    }
                }, 1000);
            }

            throw error;
        }
    }

    // 舊的等待服務器回應方法（保留作為備用）
    waitForServerResponseOld(actionType, timeout = 5000) { // 增加超時時間到 5 秒
        return new Promise((resolve, reject) => {

            if (!this.signalRConnection) {
                console.error('❌ SignalR 未連接');
                reject(new Error('SignalR 未連接'));
                return;
            }

            const timeoutId = setTimeout(() => {
                console.error(`⏰ 等待服務器回應超時: ${actionType}`);
                reject(new Error(`等待服務器回應超時: ${actionType}`));
            }, timeout);

            const responseHandler = (message) => {
                try {
                    const response = message;

                    // 檢查多種可能的回應類型
                    if (response.type === `${actionType}_response`) {

                        console.log(`✅ 匹配到回應類型: ${response.type}`);
                        clearTimeout(timeoutId);
                        // SignalR 事件监听器清理在 suiSignalr.js 中处理
                        resolve(response);
                    } else {
                        console.log(`⏳ 收到數據但類型不匹配，繼續等待...`);
                    }
                } catch (error) {
                    console.error('❌ 解析服務器回應失敗:', error);
                }
            };
        });
    }

    /**
     * 【統一方塊設置方法】- 根據類型設置方塊的所有狀態
     * @param {number} row - 行索引
     * @param {number} col - 列索引
     * @param {string} type - 方塊類型 ('random', 'heart', 'bomb', 'teleport', 'normal')
     * @param {number} value - 方塊數值
     * @param {string} logPrefix - 日誌前綴 ('生成' 或 '插入對手')
     */
    setTileAtPosition(row, col, type, value, logPrefix = '設置') {
        let tileType = TILE_TYPES.NORMAL;
        let tileValue = value;

        // 根據字串類型轉換為 TILE_TYPES 常數
        if (type === 'random') {
            tileType = TILE_TYPES.RANDOM;
            tileValue = 1; // 隨機方塊佔位值為 1
        } else if (type === 'heart') {
            tileType = TILE_TYPES.SKULL;
            tileValue = 1; // 骷髏頭方塊佔位值為 1
        } else if (type === 'bomb') {
            tileType = TILE_TYPES.BOMB;
        } else if (type === 'teleport') {
            tileType = TILE_TYPES.TELEPORT;
        }

        // 創建新的 Tile 物件
        this.board[row][col] = new Tile(tileValue, tileType);

        // 同步到舊的陣列結構（向後相容）
        this.bombBoard[row][col] = tileType === TILE_TYPES.BOMB;
        this.randomBoard[row][col] = tileType === TILE_TYPES.RANDOM;
        this.skullBoard[row][col] = tileType === TILE_TYPES.SKULL;
        this.teleportBoard[row][col] = tileType === TILE_TYPES.TELEPORT;
        this.randomDisplayBoard[row][col] = 0;
    }

    /**
     * 【轉換方塊類型】- 將 tileType 數字轉換為 TILE_TYPES 常數
     * @param {Object} tile - 方塊數據對象
     * @returns {number} 方塊類型常數 (TILE_TYPES.NORMAL, TILE_TYPES.BOMB, 等)
     */
    convertTileType(tile) {
        // tileType: 0=Normal, 1=Bomb, 2=Random, 3=Heart, 4=Teleport
        if (tile.tileType !== undefined) {
            // 直接返回數字類型（與 TILE_TYPES 一致）
            return tile.tileType;
        } else if (tile.type !== undefined) {
            // 如果是字串類型，轉換為數字
            if (tile.type === 'bomb') return TILE_TYPES.BOMB;
            if (tile.type === 'random') return TILE_TYPES.RANDOM;
            if (tile.type === 'heart') return TILE_TYPES.SKULL;
            if (tile.type === 'teleport') return TILE_TYPES.TELEPORT;
        }
        return TILE_TYPES.NORMAL;
    }

    /**
     * 【對手方塊插入】- 在指定位置插入對手方塊
     * @param {number} row - 行索引
     * @param {number} col - 列索引
     * @param {Object} tile - 方塊數據 {value, tileType 或 type}
     */
    insertOpponentTileAt(row, col, tile) {
        // ✅ 轉換 tileType 為字符串類型
        const tileType = this.convertTileType(tile);

        // 使用統一方法設置方塊
        this.setTileAtPosition(row, col, tileType, tile.value || 1, '插入對手');

        // 生成後清理無效狀態，確保空格子沒有殘留的特殊標記
        this.sanitizeBoards();

        // 強制渲染以確保畫面更新
        this.renderBoard();
    }

    /**
     * 【對手方塊動畫】- 處理對手方塊插入動畫效果（讓玩家感到"很幹"）
     * @param {number} row - 行索引
     * @param {number} col - 列索引
     * @param {Object} tile - 方塊數據 {value, tileType 或 type}
     */
    animateOpponentTileInsertion(row, col, tile) {
        const gameBoard = document.getElementById('gameBoard');
        if (!gameBoard) return;

        const cellIndex = row * 5 + col;
        const cell = gameBoard.children[cellIndex];
        if (!cell) return;

        // ✅ 轉換 tileType 為字符串類型
        const tileType = this.convertTileType(tile);

        // ✅ 使用與 addRandomTile 相同的渲染邏輯 - 添加 new-tile 動畫類
        // 根據方塊類型添加對應的動畫類（與 renderBoard 的 animateNew 邏輯一致）
        if (tileType === 'random') {
            cell.classList.add('new-random-tile');
        } else if (tileType === 'heart') {
            cell.classList.add('new-heart-tile');
        } else if (tileType === 'teleport') {
            cell.classList.add('new-teleport-tile');
        } else {
            // bomb 和 normal 都使用 new-tile
            cell.classList.add('new-tile');
        }

        // 添加對手方塊的特殊標記（用於額外效果）
        cell.classList.add('opponent-tile', 'annoying-opponent-tile');

        // 添加螢幕震動效果
        this.addScreenShake();

        // 添加煙火特效
        this.addFireworksEffect(row, col);

        // 添加嘲諷音效（如果支援）
        this.playAnnoyingSound();

        // 添加嘲諷文字提示
        this.showAnnoyingMessage(row, col);

        // 移除動畫類別（與 CSS 動畫時長匹配）
        setTimeout(() => {
            cell.classList.remove('new-tile', 'new-random-tile', 'new-heart-tile', 'new-teleport-tile');
        }, 1000);

        // 移除對手方塊特殊樣式（但保留基本樣式）
        setTimeout(() => {
            cell.classList.remove('annoying-opponent-tile', 'opponent-tile');
        }, 3000);
    }

    /**
     * 【螢幕震動效果】- 讓玩家感受到對手的"惡意"
     */
    addScreenShake() {
        const gameBoard = document.getElementById('gameBoard');
        if (!gameBoard) return;

        gameBoard.classList.add('screen-shake');

        setTimeout(() => {
            gameBoard.classList.remove('screen-shake');
        }, 500);
    }

    /**
     * 【煙火特效】- Canvas 版本的煙火動畫
     * @param {number} row - 行索引
     * @param {number} col - 列索引
     */
    addFireworksEffect(row, col) {
        const x = col * (this.cellSize + this.cellPadding) + this.cellPadding + this.cellSize / 2;
        const y = row * (this.cellSize + this.cellPadding) + this.cellPadding + this.cellSize / 2;

        // 創建煙火粒子（更多更華麗）
        for (let i = 0; i < 30; i++) {
            const angle = (Math.PI * 2 * i) / 30;
            const speed = 4 + Math.random() * 4;
            const hue = Math.random() * 60 + 300; // 紫色到紅色

            this.particleEffects.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 2, // 向上噴發
                life: 1.5,
                size: 6 + Math.random() * 6,
                color: `hsl(${hue}, 100%, 50%)`,
                isFirework: true
            });
        }
    }

    /**
     * 【嘲諷音效】- 播放令人煩躁的音效
     */
    playAnnoyingSound() {
        // 創建音效（如果瀏覽器支援）
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            // 設置令人煩躁的音調
            oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
            oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.1);
            oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.2);

            gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.3);
        } catch (error) {
            // 音效不支援時靜默處理
            console.log('Audio not supported');
        }
    }

    /**
     * 【嘲諷文字提示】- 顯示令人煩躁的訊息
     * @param {number} row - 行索引
     * @param {number} col - 列索引
     */
    showAnnoyingMessage(row, col) {
        const gameBoard = document.getElementById('gameBoard');
        if (!gameBoard) return;

        // 嘲諷訊息列表
        const annoyingMessages = [
            '😈 對手送禮物來了！',
            '💀 驚喜方塊！',
            '🔥 對手很壞！',
            '💥 意外驚喜！',
            '🎆 煙火慶祝！',
            '😱 對手攻擊！',
            '⚡ 電擊方塊！',
            '🎯 精準打擊！'
        ];

        const message = annoyingMessages[Math.floor(Math.random() * annoyingMessages.length)];

        // 創建訊息元素
        const messageElement = document.createElement('div');
        messageElement.className = 'annoying-message';
        messageElement.textContent = message;
        messageElement.style.position = 'absolute';
        messageElement.style.pointerEvents = 'none';
        messageElement.style.zIndex = '10000';
        messageElement.style.fontSize = '16px';
        messageElement.style.fontWeight = 'bold';
        messageElement.style.color = '#ff0000';
        messageElement.style.textShadow = '2px 2px 4px rgba(0,0,0,0.8)';
        messageElement.style.animation = 'annoyingMessageFloat 3s ease-out forwards';

        // 計算位置（在方塊上方）
        const cellIndex = row * 5 + col;
        const cell = gameBoard.children[cellIndex];
        const rect = cell.getBoundingClientRect();

        messageElement.style.left = (rect.left + rect.width / 2) + 'px';
        messageElement.style.top = (rect.top - 30) + 'px';
        messageElement.style.transform = 'translateX(-50%)';

        document.body.appendChild(messageElement);

        // 移除訊息
        setTimeout(() => {
            if (messageElement.parentNode) {
                messageElement.parentNode.removeChild(messageElement);
            }
        }, 3000);
    }

    /**
     * 【對手方塊插入主方法】- 將對手的方塊插入到棋盤中
     * @param {Array|Object} msg - 對手方塊數據
     */
    insertOpponentTiles(msg) {
        try {
            // 解析對手方塊數據
            const opponentTiles = msg;

            if (!Array.isArray(opponentTiles)) {
                console.error('Invalid opponent tiles format:', opponentTiles);
                return;
            }

            // 為每個對手方塊找到合適的空位並插入
            opponentTiles.forEach((tile, index) => {

                if (!tile || typeof tile.value === 'undefined') {
                    console.warn('Invalid tile data:', tile);
                    return;
                }

                // 插入對手方塊（包含sanitizeBoards和renderBoard）
                this.insertOpponentTileAt(tile.tile.i, tile.tile.j, tile);

                // 添加插入動畫效果
                setTimeout(() => {
                    this.animateOpponentTileInsertion(tile.tile.i, tile.tile.j, tile);
                }, index * 200); // 錯開動畫時間
            });

        } catch (error) {
            console.error('Error inserting opponent tiles:', error);
        }
    }

    /**
     * 【尋找空位】- 找到棋盤上的空位
     * @returns {Object|null} 空位座標 {row, col} 或 null
     */
    findEmptyPosition() {
        for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
                if (this.board[i][j].value === 0) {
                    return { row: i, col: j };
                }
            }
        }
        return null;
    }

    // 清理資源（供 React 卸載時使用）
    async destroy() {
        if (this.randomAnimationInterval) {
            clearInterval(this.randomAnimationInterval);
            this.randomAnimationInterval = null;
        }

        // 停止心跳檢測和 getNotSignEvents 排程
        this.stopHeartbeat();
        this.stopNotSignEventsScheduler();

        // 關閉 SignalR 連線
        if (this.signalRConnection) {
            await disconnectSuiSignalR();
            this.signalRConnection = null;
        }

        // 事件監聽未保存引用，無法精準移除；頁面單例不影響使用
    }

    // 防呆：清除空格的殘留旗標，避免錯誤狀態導致誤爆炸或錯誤渲染
    sanitizeBoards() {
        for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
                const tile = this.board[i][j];
                // 如果是空格，重置為普通空 Tile
                if (tile.value === 0) {
                    tile.type = TILE_TYPES.NORMAL;
                    tile.randomDisplay = 0;
                }
            }
        }
    }

    /**
     * 【棋盤初始化】- 建立乾淨的5x5遊戲陣列
     * 使用 Tile 物件陣列統一管理所有方塊資訊
     */
    initBoard() {
        // 建立5x5的空棋盤，所有位置初始為空 Tile 物件
        this.board = [];
        this.bombBoard = [];
        this.randomBoard = [];
        this.randomDisplayBoard = [];
        this.skullBoard = [];
        this.teleportBoard = [];

        for (let i = 0; i < 5; i++) {
            this.board[i] = [];
            this.bombBoard[i] = [];
            this.randomBoard[i] = [];
            this.randomDisplayBoard[i] = [];
            this.skullBoard[i] = [];
            this.teleportBoard[i] = [];

            for (let j = 0; j < 5; j++) {
                this.board[i][j] = new Tile(0, TILE_TYPES.NORMAL);
                this.bombBoard[i][j] = false;
                this.randomBoard[i][j] = false;
                this.randomDisplayBoard[i][j] = 0;
                this.skullBoard[i][j] = false;
                this.teleportBoard[i][j] = false;
            }
        }

        // 啟動隨機方塊的數字變換動畫系統
        this.startRandomAnimation();
    }

    /**
     * 【同步陣列】- 從 Tile 物件同步到舊的陣列結構（向後相容）
     */
    syncLegacyArrays() {
        for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
                const tile = this.board[i][j];
                this.bombBoard[i][j] = tile.type === TILE_TYPES.BOMB;
                this.randomBoard[i][j] = tile.type === TILE_TYPES.RANDOM;
                this.skullBoard[i][j] = tile.type === TILE_TYPES.SKULL;
                this.teleportBoard[i][j] = tile.type === TILE_TYPES.TELEPORT;
                this.randomDisplayBoard[i][j] = tile.randomDisplay;
            }
        }
    }

    // ========================================
    // 【輔助方法】- Tile 操作相關
    // ========================================

    /**
     * 創建新的 Tile 物件
     * @param {number} value - 方塊數值
     * @param {number} type - 方塊類型
     * @returns {Tile} 新的 Tile 物件
     */
    createTile(value, type = TILE_TYPES.NORMAL) {
        return new Tile(value, type);
    }

    /**
     * 複製整個棋盤（用於撤銷功能）
     * @returns {Array<Array<Tile>>} 棋盤的深拷貝
     */
    cloneBoard() {
        const newBoard = [];
        for (let i = 0; i < 5; i++) {
            newBoard[i] = [];
            for (let j = 0; j < 5; j++) {
                newBoard[i][j] = this.board[i][j].clone();
            }
        }
        return newBoard;
    }

    /**
     * 獲取指定位置的 Tile
     * @param {number} row - 行索引
     * @param {number} col - 列索引
     * @returns {Tile} Tile 物件
     */
    getTile(row, col) {
        return this.board[row][col];
    }

    /**
     * 設置指定位置的 Tile
     * @param {number} row - 行索引
     * @param {number} col - 列索引
     * @param {Tile} tile - Tile 物件
     */
    setTile(row, col, tile) {
        this.board[row][col] = tile;
    }


    /**
     * 【隨機動畫啟動】- 開始隨機方塊的數字變換週期
     * 每400ms更新一次隨機方塊的顯示數字，創造閃爍效果
     * 如果已有動畫在執行會先清除，確保只有一個定時器運作
     */
    startRandomAnimation() {
        // 防止重複啟動：先清除舊的定時器
        if (this.randomAnimationInterval) {
            clearInterval(this.randomAnimationInterval);
        }

        // 建立新的定時器：每400ms執行一次數字更新
        this.randomAnimationInterval = setInterval(() => {
            this.updateRandomNumbers();
        }, 400);
    }

    /**
     * 【亂數邏輯】- 隨機方塊的動畫循環系統
     * 隨機方塊會持續變換顯示的數字，直到玩家點擊確認
     * 動畫週期400ms，只更新顯示層不影響實際棋盤數值
     * 當沒有隨機方塊時自動停止動畫以節省效能
     */
    updateRandomNumbers() {
        let hasRandomTiles = false;

        // 遍歷棋盤尋找所有隨機方塊
        for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
                const tile = this.board[i][j];
                if (tile.type === TILE_TYPES.RANDOM) {
                    hasRandomTiles = true;
                    // 從預設數字集合中隨機選擇新的顯示數字
                    const newRandomValue = this.randomNumbers[Math.floor(Math.random() * this.randomNumbers.length)];
                    // 更新顯示緩衝區，不改變實際棋盤數值（避免影響遊戲邏輯）
                    if (newRandomValue && newRandomValue > 0) {
                        tile.randomDisplay = newRandomValue;
                    }
                }
            }
        }

        // 當沒有隨機方塊時停止動畫，釋放資源
        if (!hasRandomTiles) {
            clearInterval(this.randomAnimationInterval);
            this.randomAnimationInterval = null;
        }
    }

    /**
     * 【亂數確認】- 玩家點擊隨機方塊時的數值確認機制
     * 將隨機方塊的顯示數值寫入實際棋盤，使其變成普通可合併方塊
     * 確認後清除隨機狀態，方塊可以正常參與合併計算
     */
    async confirmRandomTile(row, col) {
        let responseOut;
        const tile = this.board[row][col];

        if (tile.type === TILE_TYPES.RANDOM) {
            try {
                // ✅ ETH版本：如果有SignalR連接就使用，否則使用本地邏輯
                if (this.signalRConnection && this.isConnected) {
                    // 發送確認請求並直接接收回應
                    const response = await this.sendPlayerActionSignalR({ type: 'confirm_random_tile', gameId: sessionStorage.getItem('gameId'), row: row, col: col });
                    responseOut = response;

                    // 更新 Tile 物件
                    this.board[row][col] = new Tile(response.tileValue, TILE_TYPES.NORMAL);

                    // 同步到舊陣列
                    this.randomBoard[row][col] = false;
                    this.randomDisplayBoard[row][col] = 0;
                    this.renderBoard();

                } else {
                    const confirmedValue = tile.randomDisplay ||
                        this.randomNumbers[Math.floor(Math.random() * this.randomNumbers.length)];

                    // 更新 Tile 物件
                    this.board[row][col] = new Tile(confirmedValue, TILE_TYPES.NORMAL);

                    // 同步到舊陣列
                    this.randomBoard[row][col] = false;
                    this.randomDisplayBoard[row][col] = 0;
                    this.renderBoard();
                }
            } catch (error) {
                console.error('❌ 確認隨機方塊失敗:', error);
            }
        }
    }

    setupEventListeners() {

        // 鍵盤事件
        document.addEventListener('keydown', (e) => {
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();
                this.handleKeyPress(e.key);
            }
        });

        // ✅ 延遲設置觸控事件，確保 DOM 已經渲染
        setTimeout(() => {
            this.setupTouchControls();
        }, 100);
    }

    // 【設置觸控控制】- 獨立方法，可重複調用，帶重試機制
    setupTouchControls(retryCount = 0) {
        const gameBoard = document.getElementById('gameBoard');
        const swipeIndicator = document.getElementById('swipeIndicator');

        if (!gameBoard) {
            // 如果 gameBoard 不存在且重試次數未達上限，延遲後重試
            if (retryCount < 20) {
                console.log(`⏳ gameBoard 尚未就緒，${100}ms 後重試... (${retryCount + 1}/20)`);
                setTimeout(() => {
                    this.setupTouchControls(retryCount + 1);
                }, 100);
            } else {
                console.error('❌ 無法找到 gameBoard 元素，觸控控制設置失敗');
            }
            return;
        }

        // 檢查是否已經設置過觸控事件
        if (gameBoard.dataset.touchInitialized === 'true') {
            return;
        }

        console.log('✅ 觸控控制設置成功');

        // 觸控事件
        let startX, startY, startTime;
        let lastTouchTime = 0;
        let lastTouchCell = null;
        const doubleTouchDelay = 200; // 雙擊間隔時間（毫秒）

        gameBoard.addEventListener('touchstart', (e) => {
            e.preventDefault();
            const touch = e.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
            startTime = Date.now();
            gameBoard.classList.add('swiping');
        }, { passive: false });

        gameBoard.addEventListener('touchmove', (e) => {
            // 始終阻止預設滾動行為，確保觸控事件由遊戲處理
            e.preventDefault();
            if (!startX || !startY) return;
            const touch = e.touches[0];
            const currentX = touch.clientX;
            const currentY = touch.clientY;
            const deltaX = currentX - startX;
            const deltaY = currentY - startY;
            if (Math.abs(deltaX) > 20 || Math.abs(deltaY) > 20) {
                let direction = '';
                if (Math.abs(deltaX) > Math.abs(deltaY)) {
                    direction = deltaX > 0 ? '→' : '←';
                } else {
                    direction = deltaY > 0 ? '↓' : '↑';
                }
                if (swipeIndicator) {
                    swipeIndicator.textContent = direction;
                    swipeIndicator.classList.add('show');
                }
            }
        }, { passive: false });

        gameBoard.addEventListener('touchend', (e) => {
            gameBoard.classList.remove('swiping');
            if (swipeIndicator) swipeIndicator.classList.remove('show');

            const touch = e.changedTouches[0];
            const endX = touch.clientX;
            const endY = touch.clientY;
            const endTime = Date.now();

            // 檢查是否點擊了 random-tile
            const element = document.elementFromPoint(endX, endY);
            const cell = element ? element.closest('.game-cell') : null;

            if (cell && cell.classList.contains('random-tile')) {
                const currentTime = Date.now();
                const row = parseInt(cell.dataset.row);
                const col = parseInt(cell.dataset.col);

                // 檢查是否為雙擊
                if (lastTouchCell === cell && (currentTime - lastTouchTime) < doubleTouchDelay) {
                    // 雙擊：執行 confirmRandomTile
                    this.confirmRandomTile(row, col);
                    // 重置雙擊檢測
                    lastTouchTime = 0;
                    lastTouchCell = null;
                    startX = startY = null;
                    return;
                } else {
                    // 單擊：記錄點擊資訊
                    lastTouchTime = currentTime;
                    lastTouchCell = cell;

                    // 延遲執行單擊效果（如果沒有雙擊的話）
                    setTimeout(() => {
                        if (lastTouchTime === currentTime) {
                            // 這裡可以添加單擊效果，比如高亮顯示
                            cell.classList.add('single-touch-highlight');
                            setTimeout(() => {
                                cell.classList.remove('single-touch-highlight');
                            }, 200);
                        }
                    }, doubleTouchDelay);
                }
            }

            // 【滑動判斷邏輯】
            if (!startX || !startY) return;

            const deltaX = endX - startX;
            const deltaY = endY - startY;
            const deltaTime = endTime - startTime;
            const absDeltaX = Math.abs(deltaX);
            const absDeltaY = Math.abs(deltaY);
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
            const minSwipeDistance = 20;  // 降低最小距離閾值
            const maxSwipeTime = 1000;    // 增加允許的滑動時間

            // 檢查：滑動時間不能太長
            if (deltaTime > maxSwipeTime) {
                startX = startY = null;
                return;
            }

            // 檢查：滑動距離必須足夠
            if (distance < minSwipeDistance) {
                console.log('📏 滑動距離過短，忽略 (' + distance.toFixed(1) + 'px)');
                startX = startY = null;
                return;
            }

            // 【方向判斷】- 簡化邏輯，以主要方向為準
            let direction = null;

            if (absDeltaX > absDeltaY) {
                // 水平滑動為主
                direction = deltaX > 0 ? 'right' : 'left';
            } else {
                // 垂直滑動為主
                direction = deltaY > 0 ? 'down' : 'up';
            }

            // 執行移動
            if (direction) {
                this.move(direction);
            }

            startX = startY = null;
        }, { passive: false });

        // 移除全域阻止滾動的 listener，避免 Safari/行動裝置誤觸

        // 雙擊檢測變數
        let lastClickTime = 0;
        let lastClickCell = null;
        const doubleClickDelay = 200; // 雙擊間隔時間（毫秒）

        // 添加随机数方块点击事件（支援單擊和雙擊）
        gameBoard.addEventListener('click', (e) => {
            const cell = e.target.closest('.game-cell');
            if (cell && cell.classList.contains('random-tile')) {
                const currentTime = Date.now();
                const row = parseInt(cell.dataset.row);
                const col = parseInt(cell.dataset.col);

                // 檢查是否為雙擊
                if (lastClickCell === cell && (currentTime - lastClickTime) < doubleClickDelay) {
                    // 雙擊：執行 confirmRandomTile
                    this.confirmRandomTile(row, col);
                    // 重置雙擊檢測
                    lastClickTime = 0;
                    lastClickCell = null;
                } else {
                    // 單擊：記錄點擊資訊
                    lastClickTime = currentTime;
                    lastClickCell = cell;

                    // 延遲執行單擊效果（如果沒有雙擊的話）
                    setTimeout(() => {
                        if (lastClickTime === currentTime) {
                            // 這裡可以添加單擊效果，比如高亮顯示
                            cell.classList.add('single-click-highlight');
                            setTimeout(() => {
                                cell.classList.remove('single-click-highlight');
                            }, 200);
                        }
                    }, doubleClickDelay);
                }
            }
        });

        // 標記已初始化
        gameBoard.dataset.touchInitialized = 'true';
    }

    handleKeyPress(key) {
        // 防抖檢查
        if (this.isDebounceBlocked()) return;

        switch (key) {
            case 'ArrowUp': this.move('up'); break;
            case 'ArrowDown': this.move('down'); break;
            case 'ArrowLeft': this.move('left'); break;
            case 'ArrowRight': this.move('right'); break;
        }
    }

    /**
     * 【嚴格狀態機核心】- 改變遊戲狀態並記錄歷史，確保狀態完成才能轉換
     */
    setState(newState, data = {}) {
        const oldState = this.gameState;

        // 檢查狀態轉換鎖定
        if (this.stateTransitionLocked && newState !== Game20Engine.STATES.IDLE) {
            console.warn(`🔒 狀態轉換被鎖定: 正在處理 ${oldState}`);
            return false;
        }

        // 檢查當前狀態是否已完成
        if (!this.isStateComplete && !this.canForceTransition(oldState, newState)) {
            console.warn(`⏳ 狀態未完成，無法轉換: ${oldState} → ${newState}`);
            return false;
        }

        // 驗證狀態轉換是否有效
        if (!this.isValidStateTransition(oldState, newState)) {
            console.warn(`⚠️ 無效的狀態轉換: ${oldState} → ${newState}`);
            return false;
        }

        // 完成當前狀態的清理工作
        this.onStateExit(oldState);

        // 記錄狀態歷史
        const stateRecord = {
            from: oldState,
            to: newState,
            timestamp: Date.now(),
            duration: oldState === Game20Engine.STATES.IDLE ? 0 : Date.now() - this.stateStartTime,
            data: data
        };

        this.stateHistory.push(stateRecord);

        // 更新狀態
        this.gameState = newState;
        this.stateStartTime = Date.now();
        this.isStateComplete = false; // 新狀態未完成

        // 狀態進入處理
        this.onStateEnter(newState, data);

        return true;
    }

    /**
     * 【狀態退出處理】- 當離開狀態時的清理工作
     */
    onStateExit(state) {
        const { STATES } = Game20Engine;

        // 清除該狀態的所有計時器
        this.clearStateTimeouts();

        switch (state) {
            case STATES.MOVING:
                // 確保移動動畫完成
                break;
            case STATES.EXPLODING:
                // 確保爆炸效果清理
                const gameBoard = document.getElementById('gameBoard');
                if (gameBoard) {
                    gameBoard.classList.remove('exploding-shake');
                }
                break;
        }
    }

    /**
     * 【狀態完成標記】- 標記當前狀態為已完成
     */
    completeCurrentState() {
        this.isStateComplete = true;
        this.stateTransitionLocked = false;
    }

    /**
     * 【狀態鎖定控制】- 鎖定狀態轉換
     */
    lockStateTransition() {
        this.stateTransitionLocked = true;
    }

    unlockStateTransition() {
        this.stateTransitionLocked = false;
    }

    /**
     * 【強制轉換檢查】- 某些情況下允許強制轉換
     */
    canForceTransition(from, to) {
        const { STATES } = Game20Engine;

        // 錯誤恢復：允許從任何狀態回到 IDLE
        if (to === STATES.IDLE) return true;

        // 遊戲結束：允許從任何狀態到 GAME_OVER
        if (to === STATES.GAME_OVER) return true;

        return false;
    }

    /**
     * 【狀態計時器管理】- 管理狀態相關的計時器
     */
    addStateTimeout(callback, delay, description = '') {
        const timeoutId = setTimeout(() => {
            callback();
        }, delay);

        this.stateTimeouts.push({
            id: timeoutId,
            description: description,
            delay: delay
        });

        return timeoutId;
    }

    clearStateTimeouts() {
        this.stateTimeouts.forEach(timeout => {
            clearTimeout(timeout.id);
        });
        this.stateTimeouts = [];
    }

    /**
     * 【狀態轉換驗證】- 檢查狀態轉換是否合法
     */
    isValidStateTransition(from, to) {
        const { STATES } = Game20Engine;

        const validTransitions = {
            [STATES.IDLE]: [STATES.MOVING, STATES.GAME_OVER, STATES.PAUSED],
            [STATES.MOVING]: [STATES.PROCESSING, STATES.IDLE],
            [STATES.PROCESSING]: [STATES.EXPLODING, STATES.ADDING_TILE, STATES.IDLE],
            [STATES.EXPLODING]: [STATES.ADDING_TILE, STATES.IDLE],
            [STATES.ADDING_TILE]: [STATES.IDLE, STATES.GAME_OVER],
            [STATES.GAME_OVER]: [STATES.IDLE],
            [STATES.PAUSED]: [STATES.IDLE, STATES.GAME_OVER]
        };

        return validTransitions[from]?.includes(to) || false;
    }

    /**
     * 【狀態進入處理】- 當進入新狀態時的處理邏輯，確保每個狀態有明確的完成機制
     */
    onStateEnter(state, data) {
        const { STATES } = Game20Engine;

        switch (state) {
            case STATES.IDLE:
                this.onIdleEnter();
                break;
            case STATES.MOVING:
                this.onMovingEnterSignalR(data);
                break;
            case STATES.PROCESSING:
                this.onProcessingEnter(data);
                break;
            case STATES.EXPLODING:
                this.onExplodingEnter(data);
                break;
            case STATES.ADDING_TILE:
                this.onAddingTileEnter(data);
                break;
            case STATES.GAME_OVER:
                this.onGameOverEnter(data);
                break;
            case STATES.PAUSED:
                this.onPausedEnter();
                break;
        }
    }

    /**
     * 【IDLE狀態進入】- 重置防抖，允許新的用戶輸入（立即完成）
     */
    onIdleEnter() {
        this.isMoving = false;
        this.isActionLocked = false;
        this.isExploding = false;

        // 移除視覺效果
        const gameBoard = document.getElementById('gameBoard');
        if (gameBoard) {
            gameBoard.classList.remove('moving', 'exploding-shake');
        }

        // IDLE 狀態立即完成
        this.completeCurrentState();
    }

    async onMovingEnterSignalR(data) {
        this.isMoving = true;
        this.isActionLocked = true;
        this.lastMoveTime = Date.now();

        // 鎖定狀態轉換，直到移動完成
        this.lockStateTransition();

        const { direction } = data;
        const gameBoard = document.getElementById('gameBoard');
        if (gameBoard) gameBoard.classList.add('moving');

        // ✅ 保存移動前的棋盤狀態
        this.saveBoardState();

        // 執行實際的移動邏輯 TODO : 回傳Line and explosions只要會動到前端顯示board都要回傳。
        const moveResult = await this.sendPlayerActionSignalR(
            {
                type: 'move',
                multiplayerGameId: sessionStorage.getItem('multiplayer_game_id'),
                gameId: sessionStorage.getItem('gameId'),
                direction: direction
            });

        // ✅ 檢查移動結果是否有效
        if (!moveResult || !moveResult.moveData) {
            this.completeCurrentState();
            return;
        }

        let result = moveResult.moveData;
        let addTileData = moveResult.addTileData;
        let moved = false;
        this.score = result.score;

        if (direction === 'up' || direction === 'down') {
            for (let j = 0; j < 5; j++) {

                const oldVals = [...this.board[j]];
                const oldBombs = [...this.bombBoard[j]];
                const oldRandoms = [...this.randomBoard[j]];
                const oldHearts = [...this.skullBoard[j]];

                const newLine = result.newLine.slice(j * 5, (j + 1) * 5);
                const newBombs = result.newBombs.slice(j * 5, (j + 1) * 5);
                const newRandoms = result.newRandoms.slice(j * 5, (j + 1) * 5);
                const newSkulls = result.newSkulls.slice(j * 5, (j + 1) * 5);
                const newTeleports = result.newTeleports ? result.newTeleports.slice(j * 5, (j + 1) * 5) : [false, false, false, false, false];

                const changed = !this.arraysEqual(oldVals, newLine) ||
                    !this.arraysEqual(oldBombs, newBombs) ||
                    !this.arraysEqual(oldRandoms, newRandoms) ||
                    !this.arraysEqual(oldHearts, newSkulls);
                if (changed) {
                    moved = true;
                }
                this.setColumn(j, newLine, newBombs, newRandoms, newSkulls, newTeleports);
            }
        } else {
            for (let i = 0; i < 5; i++) {
                const oldVals = [...this.board[i]];
                const oldBombs = [...this.bombBoard[i]];
                const oldRandoms = [...this.randomBoard[i]];
                const oldHearts = [...this.skullBoard[i]];

                const newLine = result.newLine.slice(i * 5, (i + 1) * 5);
                const newBombs = result.newBombs.slice(i * 5, (i + 1) * 5);
                const newRandoms = result.newRandoms.slice(i * 5, (i + 1) * 5);
                const newSkulls = result.newSkulls.slice(i * 5, (i + 1) * 5);
                const newTeleports = result.newTeleports ? result.newTeleports.slice(i * 5, (i + 1) * 5) : [false, false, false, false, false];

                const changed = !this.arraysEqual(oldVals, newLine) ||
                    !this.arraysEqual(oldBombs, newBombs) ||
                    !this.arraysEqual(oldRandoms, newRandoms) ||
                    !this.arraysEqual(oldHearts, newSkulls);
                if (changed) {
                    moved = true;
                }

                this.setRow(i, newLine, newBombs, newRandoms, newSkulls, newTeleports);
            }
        }


        // ✅ 立即應用預取的方塊數據（提前生成），實現與移動動畫同步出現
        // let tileAlreadyApplied = false;
        // if (addTileData) {
        //     tileAlreadyApplied = this.applyAddTileData(addTileData);
        // }

        // ✅ 計算方塊移動動畫並渲染
        if (moved) {
            this.calculateTileMovements(direction);
            this.updateDisplay();
            this.renderBoard();
        }

        if (moved) {
            // 移動成功，設定動畫完成計時器
            this.addStateTimeout(() => {
                this.completeCurrentState();
                this.setState(Game20Engine.STATES.PROCESSING, {
                    moveResult: {
                        moved,
                        explosions: result.explosions
                    },
                    addTileData: addTileData
                });
            }, this.speedConfig.transitionDelay, 'MOVING動畫完成');
        } else {
            // 沒有移動，立即解鎖並回到 IDLE 狀態
            this.completeCurrentState();
            this.setState(Game20Engine.STATES.IDLE);
        }
    }

    /**
     * 【PROCESSING狀態進入】- 處理合併結果（等待處理完成）
     */
    onProcessingEnter(data) {
        const { moveResult } = data;

        this.lockStateTransition();

        // 更新分數和合併效果
        if (moveResult.score) {
            this.score += moveResult.score * this.VALUE_MULTIPLIER;
        }

        // 根據是否有爆炸決定下一個狀態
        this.addStateTimeout(() => {
            this.completeCurrentState();

            if (moveResult.explosions && moveResult.explosions.length > 0) {
                this.setState(Game20Engine.STATES.EXPLODING, {
                    explosions: moveResult.explosions,
                    addTileData: data.addTileData,
                });
            } else {
                this.setState(Game20Engine.STATES.ADDING_TILE, {
                    addTileData: data.addTileData,
                });
            }
        }, this.speedConfig.transitionDelay, 'PROCESSING處理完成');
    }

    /**
     * 【EXPLODING狀態進入】- 處理爆炸效果（等待爆炸完成）
     */
    onExplodingEnter(data) {
        const { explosions } = data;
        this.isExploding = true;

        this.lockStateTransition();

        const gameBoard = document.getElementById('gameBoard');
        if (gameBoard) gameBoard.classList.add('exploding-shake');

        // 執行爆炸邏輯
        const explosionResult = this.executeExplosions(explosions);

        // 爆炸動畫時間 - 使用 explosionFinish 確保所有爆炸效果都完成
        this.addStateTimeout(() => {
            this.isExploding = false;
            if (gameBoard) gameBoard.classList.remove('exploding-shake');

            this.completeCurrentState();
            this.setState(Game20Engine.STATES.ADDING_TILE, {
                explosionResult,
                addTileData: data.addTileData,
            });
        }, this.speedConfig.explosionFinish, 'EXPLODING動畫完成');
    }

    /**
     * 【ADDING_TILE狀態進入】- 添加新方塊（等待添加完成）
     */
    async onAddingTileEnter(data) {
        this.lockStateTransition();

        let tileAdded = false;

        // 如果已經有預取的方塊數據（SignalR 模式的新流程）
        if (data?.addTileData) {
            // 如果在之前的階段（如 MOVING）尚未應用過，則在此應用
            if (!data.tileAlreadyApplied) {
                tileAdded = this.applyAddTileData(data.addTileData);
            } else {
                tileAdded = true; // 已在先前應用，僅標記成功以完成狀態
            }
        } else {
            // ✅ 正統流程：根據SignalR連接狀態選擇處理方式
            tileAdded = await this.addRandomTileSignalR();
        }

        // 如果有添加方塊，使用動畫效果渲染
        if (tileAdded) {
            this.renderBoard(true);
        }

        // 添加完成後檢查遊戲狀態
        this.addStateTimeout(() => {
            this.completeCurrentState();

            // 檢查遊戲結束條件：無法添加新方塊且無法移動
            if (!tileAdded && this.isGameOver()) {
                this.setState(Game20Engine.STATES.GAME_OVER, { reason: '無法繼續遊戲' });
            } else if (this.isGameOver()) {
                this.setState(Game20Engine.STATES.GAME_OVER, { reason: '無法繼續移動' });
            } else {
                this.setState(Game20Engine.STATES.IDLE);
            }
        }, this.speedConfig.addingTileDelay, 'ADDING_TILE完成');
    }

    /**
     * 【應用添加方塊數據】- 將後端預先生成的方塊資訊應用到棋盤
     * 用於優化後的 SignalR 流程
     */
    applyAddTileData(addTileData) {
        if (!addTileData || !addTileData.tile) {
            return false;
        }

        const { tile, isRandom, isHeart, isBomb, bombValue, tileValue, isTeleport, teleportValue } = addTileData;
        const { i, j } = tile;

        // 使用統一方法設置方塊
        if (isRandom) {
            this.setTileAtPosition(i, j, 'random', 1, '預取生成');
        } else if (isHeart) {
            this.setTileAtPosition(i, j, 'heart', 1, '預取生成');
        } else if (isBomb) {
            this.setTileAtPosition(i, j, 'bomb', bombValue, '預取生成');
        } else if (isTeleport) {
            this.setTileAtPosition(i, j, 'teleport', 1, '預取生成');
        } else {
            this.setTileAtPosition(i, j, 'normal', tileValue, '預取生成');
        }

        // 生成後清理無效狀態，確保空格子沒有殘留的特殊標記
        this.sanitizeBoards();
        return true;
    }

    /**
     * 【GAME_OVER狀態進入】- 遊戲結束處理（立即完成）
     */
    onGameOverEnter(data) {
        this.gameOverFlag = true; // 重命名以避免與 isGameOver() 方法衝突

        // 顯示遊戲結束畫面
        const isWin = this.checkWin();
        const message = data?.reason || (isWin ? '恭喜獲勝！' : '無法繼續遊戲了！');
        this.showGameOver(isWin, message);

        // 遊戲結束狀態立即完成
        this.completeCurrentState();
    }

    /**
     * 【PAUSED狀態進入】- 遊戲暫停處理（立即完成）
     */
    onPausedEnter() {
        this.completeCurrentState();
    }

    /**
     * 【狀態檢查】- 檢查是否可以執行某個動作
     */
    canMove() {
        return this.gameState === Game20Engine.STATES.IDLE && !this.isDebounceBlocked();
    }

    canAddTile() {
        return this.gameState === Game20Engine.STATES.ADDING_TILE;
    }

    canExplode() {
        return this.gameState === Game20Engine.STATES.EXPLODING;
    }

    /**
     * 【防抖檢查】- 防止短時間內重複觸發移動
     * 檢查是否在防抖延遲時間內或正在移動中
     */
    isDebounceBlocked() {
        const now = Date.now();
        const timeSinceLastMove = now - this.lastMoveTime;

        // 如果距離上次移動時間太短，或正在移動中，則阻止
        if (timeSinceLastMove < this.speedConfig.moveDebounce || this.isMoving) {
            return true;
        }

        return false;
    }

    /**
     * 【重構的移動方法】- 使用狀態機控制
     */
    move(direction) {
        // 狀態機檢查：只有在IDLE狀態且未被防抖阻止時才允許移動
        if (!this.canMove()) {
            console.log(`🚫 無法移動: gameState=${this.gameState}, isMoving=${this.isMoving}, isDebounceBlocked=${this.isDebounceBlocked()}`);
            return;
        }

        // 保存狀態
        this.saveState();

        // 進入移動狀態
        this.setState(Game20Engine.STATES.MOVING, { direction });
    }

    /**
     * 【執行移動邏輯】- 從狀態機中分離出的純移動邏輯
     */
    executeMove(direction) {
        let moved = false;
        let explosions = [];

        // 原有的移動邏輯
        if (direction === 'right') {
            for (let i = 0; i < 5; i++) {
                const oldVals = [...this.board[i]];
                const oldBombs = [...this.bombBoard[i]];
                const oldRandoms = [...this.randomBoard[i]];
                const oldHearts = [...this.skullBoard[i]];
                const oldTeleports = [...this.teleportBoard[i]];
                const result = this.processLineWithBombs(this.getRow(i), true);
                const changed = !this.arraysEqual(oldVals, result.newLine) ||
                    !this.arraysEqual(oldBombs, result.newBombs) ||
                    !this.arraysEqual(oldRandoms, result.newRandoms) ||
                    !this.arraysEqual(oldHearts, result.newSkulls) ||
                    !this.arraysEqual(oldTeleports, result.newTeleports);
                if (changed) moved = true;
                explosions.push(...result.explosions.map(pos => ({ row: i, col: pos })));
                this.setRow(i, result.newLine, result.newBombs, result.newRandoms, result.newSkulls, result.newTeleports);
            }
        } else if (direction === 'left') {
            for (let i = 0; i < 5; i++) {
                const oldVals = [...this.board[i]];
                const oldBombs = [...this.bombBoard[i]];
                const oldRandoms = [...this.randomBoard[i]];
                const oldHearts = [...this.skullBoard[i]];
                const oldTeleports = [...this.teleportBoard[i]];
                const result = this.processLineWithBombs(this.getRow(i), false);
                const changed = !this.arraysEqual(oldVals, result.newLine) ||
                    !this.arraysEqual(oldBombs, result.newBombs) ||
                    !this.arraysEqual(oldRandoms, result.newRandoms) ||
                    !this.arraysEqual(oldHearts, result.newSkulls) ||
                    !this.arraysEqual(oldTeleports, result.newTeleports);
                if (changed) moved = true;
                explosions.push(...result.explosions.map(pos => ({ row: i, col: pos })));
                this.setRow(i, result.newLine, result.newBombs, result.newRandoms, result.newSkulls, result.newTeleports);
            }
        } else if (direction === 'up') {
            for (let j = 0; j < 5; j++) {
                const oldVals = [this.board[0][j], this.board[1][j], this.board[2][j], this.board[3][j], this.board[4][j]];
                const oldBombs = [this.bombBoard[0][j], this.bombBoard[1][j], this.bombBoard[2][j], this.bombBoard[3][j], this.bombBoard[4][j]];
                const oldRandoms = [this.randomBoard[0][j], this.randomBoard[1][j], this.randomBoard[2][j], this.randomBoard[3][j], this.randomBoard[4][j]];
                const oldHearts = [this.skullBoard[0][j], this.skullBoard[1][j], this.skullBoard[2][j], this.skullBoard[3][j], this.skullBoard[4][j]];
                const oldTeleports = [this.teleportBoard[0][j], this.teleportBoard[1][j], this.teleportBoard[2][j], this.teleportBoard[3][j], this.teleportBoard[4][j]];

                const result = this.processLineWithBombs(this.getColumn(j), false);
                const changed = !this.arraysEqual(oldVals, result.newLine) ||
                    !this.arraysEqual(oldBombs, result.newBombs) ||
                    !this.arraysEqual(oldRandoms, result.newRandoms) ||
                    !this.arraysEqual(oldHearts, result.newSkulls) ||
                    !this.arraysEqual(oldTeleports, result.newTeleports);
                if (changed) moved = true;
                explosions.push(...result.explosions.map(pos => ({ row: pos, col: j })));
                this.setColumn(j, result.newLine, result.newBombs, result.newRandoms, result.newSkulls, result.newTeleports);
            }
        } else if (direction === 'down') {
            for (let j = 0; j < 5; j++) {
                const oldVals = [this.board[0][j], this.board[1][j], this.board[2][j], this.board[3][j], this.board[4][j]];
                const oldBombs = [this.bombBoard[0][j], this.bombBoard[1][j], this.bombBoard[2][j], this.bombBoard[3][j], this.bombBoard[4][j]];
                const oldRandoms = [this.randomBoard[0][j], this.randomBoard[1][j], this.randomBoard[2][j], this.randomBoard[3][j], this.randomBoard[4][j]];
                const oldHearts = [this.skullBoard[0][j], this.skullBoard[1][j], this.skullBoard[2][j], this.skullBoard[3][j], this.skullBoard[4][j]];
                const oldTeleports = [this.teleportBoard[0][j], this.teleportBoard[1][j], this.teleportBoard[2][j], this.teleportBoard[3][j], this.teleportBoard[4][j]];

                const result = this.processLineWithBombs(this.getColumn(j), true);
                const changed = !this.arraysEqual(oldVals, result.newLine) ||
                    !this.arraysEqual(oldBombs, result.newBombs) ||
                    !this.arraysEqual(oldRandoms, result.newRandoms) ||
                    !this.arraysEqual(oldHearts, result.newSkulls) ||
                    !this.arraysEqual(oldTeleports, result.newTeleports);
                if (changed) moved = true;
                explosions.push(...result.explosions.map(pos => ({ row: pos, col: j })));
                this.setColumn(j, result.newLine, result.newBombs, result.newRandoms, result.newSkulls, result.newTeleports);
            }
        }

        if (moved) {
            this.moves++;
            this.updateDisplay();
            this.renderBoard();
        } else {
            // 即使沒有移動也要渲染，確保畫面同步
            this.renderBoard();
        }

        return { moved, explosions };
    }

    /**
     * 【執行爆炸邏輯】- 從狀態機中分離出的純爆炸邏輯
     */
    executeExplosions(explosions) {
        const gameBoard = document.getElementById('gameBoard');

        // 添加震動效果
        if (gameBoard) gameBoard.classList.add('exploding-shake');

        // 為每個爆炸點創建視覺效果
        for (const explosion of explosions) {
            this.showExplosionAnimation(explosion.row, explosion.col);
            this.createExplosionWave(explosion.row, explosion.col);
        }

        // 使用配置中的延遲開始實際清除方塊
        setTimeout(() => {
            for (const explosion of explosions) {
                this.explodeAt(explosion.row, explosion.col);
            }
        }, this.speedConfig.explosionDelay);

        // 使用配置中的延遲移除震動效果
        setTimeout(() => {
            if (gameBoard) gameBoard.classList.remove('exploding-shake');
        }, this.speedConfig.explosionShake);
    }

    /**
     * 【爆炸邏輯】- 炸彈方塊的爆炸機制處理
     * 當兩個炸彈方塊合併或炸彈與普通方塊合併時觸發爆炸
     * 爆炸會在延遲後清除爆炸點周圍的區域（十字形或方形）
     * 支援多個同時爆炸點，每個爆炸都有獨立的擴散動畫
     * 如果愛心方塊被炸到，遊戲結束
     */
    handleExplosions(explosions) {
        const gameBoard = document.getElementById('gameBoard');
        // 鎖定操作防止爆炸期間的誤觸
        this.isActionLocked = true;
        this.isExploding = true;

        // 添加震動效果
        if (gameBoard) gameBoard.classList.add('exploding-shake');

        // 檢查是否有愛心方塊被炸到
        let heartExploded = false;
        for (const explosion of explosions) {
            if (this.skullBoard[explosion.row][explosion.col]) {
                heartExploded = true;
                break;
            }
        }

        // 為每個爆炸點創建視覺效果和擴散波動畫
        for (const explosion of explosions) {
            this.showExplosionAnimation(explosion.row, explosion.col);
            this.createExplosionWave(explosion.row, explosion.col);
        }

        // 使用配置中的延遲開始實際清除方塊（給視覺效果時間）
        setTimeout(() => {
            for (const explosion of explosions) {
                this.explodeAt(explosion.row, explosion.col);
            }

            // 如果愛心方塊被炸到，遊戲結束
            if (heartExploded) {
                setTimeout(() => {
                    this.showGameOver(false, 'The skull block was destroyed!');
                }, this.speedConfig.explosionDelay);
                return;
            }

            // 使用配置中的延遲完成爆炸，恢復遊戲狀態
            setTimeout(() => {
                this.renderBoard();
                this.isExploding = false;
                this.isActionLocked = false;

                // 使用狀態機處理添加新方塊
                if (this.gameState === Game20Engine.STATES.EXPLODING) {
                    this.completeCurrentState();
                    this.setState(Game20Engine.STATES.ADDING_TILE);
                }
            }, this.speedConfig.explosionFinish);
        }, this.speedConfig.explosionDelay);

        // 使用配置中的延遲移除震動效果
        setTimeout(() => {
            if (gameBoard) gameBoard.classList.remove('exploding-shake');
        }, this.speedConfig.explosionShake);
    }

    showExplosionAnimation(row, col) {
        const cells = document.querySelectorAll('.game-cell');
        const cellIndex = row * 5 + col;
        if (cells[cellIndex]) {
            cells[cellIndex].classList.add('exploding');
        }
    }

    createExplosionWave(row, col) {
        // Canvas 版本的爆炸波效果
        this.addExplosionEffect(row, col);

        // 添加圓形擴散波動畫
        const x = col * (this.cellSize + this.cellPadding) + this.cellPadding + this.cellSize / 2;
        const y = row * (this.cellSize + this.cellPadding) + this.cellPadding + this.cellSize / 2;

        this.tileAnimations.push({
            type: 'explosion_wave',
            x,
            y,
            radius: 0,
            maxRadius: this.cellSize * 2,
            progress: 0,
            alpha: 1
        });
    }

    /**
     * 【爆炸範圍計算】- 根據爆炸模式清除指定區域的方塊
     * explosionMode控制爆炸形狀：'cross'=十字形, 'square'=方形
     * explosionRange控制爆炸範圍大小（預設為1格）
     * 爆炸會按距離順序逐步清除方塊，產生擴散效果
     * 如果愛心方塊被炸到，遊戲結束
     */
    explodeAt(centerRow, centerCol) {
        let directions = [];

        // 根據爆炸模式計算影響的座標偏移
        if (this.explosionMode === 'cross') {
            // 十字形爆炸：橫向和縱向各擴散explosionRange格
            for (let i = -this.explosionRange; i <= this.explosionRange; i++) {
                directions.push([i, 0]); // 縱向
                if (i !== 0) directions.push([0, i]); // 橫向（避免重複中心點）
            }
        } else if (this.explosionMode === 'square') {
            // 方形爆炸：以爆炸點為中心的正方形區域
            for (let i = -this.explosionRange; i <= this.explosionRange; i++) {
                for (let j = -this.explosionRange; j <= this.explosionRange; j++) {
                    directions.push([i, j]);
                }
            }
        }

        // 按距離排序，讓爆炸從中心向外擴散
        const sortedDirections = directions.map(([dRow, dCol]) => ({
            dRow, dCol,
            distance: Math.abs(dRow) + Math.abs(dCol)
        })).sort((a, b) => a.distance - b.distance);

        // ✅ 在執行爆炸清除之前，先保存所有受影響格子的 skull 狀態
        // 這樣可以避免新生成的 skull（在 T=150ms 添加）被誤判為被炸毀的 skull（在 T=200-228ms 檢查）
        const skullStates = new Map();
        sortedDirections.forEach(({ dRow, dCol }) => {
            const newRow = centerRow + dRow;
            const newCol = centerCol + dCol;
            if (newRow >= 0 && newRow < 5 && newCol >= 0 && newCol < 5) {
                const key = `${newRow},${newCol}`;
                skullStates.set(key, this.board[newRow][newCol].type === TILE_TYPES.SKULL);
            }
        });

        // 按順序清除每個受影響的格子
        sortedDirections.forEach(({ dRow, dCol, distance }) => {
            const newRow = centerRow + dRow;
            const newCol = centerCol + dCol;

            // 確保座標在棋盤範圍內
            if (newRow >= 0 && newRow < 5 && newCol >= 0 && newCol < 5) {
                const key = `${newRow},${newCol}`;
                const wasSkullBeforeExplosion = skullStates.get(key);

                // 根據距離延遲執行，創造擴散效果
                setTimeout(() => {
                    const tile = this.board[newRow][newCol];

                    // ✅ 金色方塊不會被炸彈摧毀（使用 Tile 的 isGold() 方法）
                    if (tile && tile.isGold()) {
                        console.log(`💛 金色方塊 [${newRow}][${newCol}] (value=${tile.value}) 受到爆炸保護，未被摧毀`);
                        return;
                    }

                    // 清除該位置的所有狀態 - 使用 Tile 物件
                    this.board[newRow][newCol] = new Tile(0, TILE_TYPES.NORMAL);
                    this.bombBoard[newRow][newCol] = false;
                    this.randomBoard[newRow][newCol] = false; // 防止隨機方塊殘留
                    this.skullBoard[newRow][newCol] = false; // 清除骷髏方塊

                    // ✅ 使用爆炸前保存的狀態來判斷，而不是當前狀態
                    // 這樣可以避免新生成的 skull 被誤判為被炸毀的 skull
                    if (wasSkullBeforeExplosion) {
                        setTimeout(() => {
                            this.showGameOver(false, '骷髏方塊被炸毀了！');
                        }, 100);
                        return;
                    }

                    // 添加視覺效果 - Canvas 版本
                    this.createMiniExplosionWave(newRow, newCol);

                    // 重新渲染棋盤
                    this.renderBoard();
                }, distance * 20); // 每格延遲20ms，創造波浪式擴散
            }
        });
    }

    createMiniExplosionWave(row, col) {
        // Canvas 版本的小型爆炸波效果
        const x = col * (this.cellSize + this.cellPadding) + this.cellPadding + this.cellSize / 2;
        const y = row * (this.cellSize + this.cellPadding) + this.cellPadding + this.cellSize / 2;

        // 添加小型粒子效果
        for (let i = 0; i < 10; i++) {
            const angle = (Math.PI * 2 * i) / 10;
            const speed = 1.5 + Math.random() * 1.5;
            this.particleEffects.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                life: 0.8,
                size: 3 + Math.random() * 3,
                color: `rgba(255, ${Math.floor(Math.random() * 100 + 100)}, 0, ${0.6})`
            });
        }
    }

    moveRight() { return this.processMove('right'); }
    moveLeft() { return this.processMove('left'); }
    moveUp() { return this.processMove('up'); }
    moveDown() { return this.processMove('down'); }

    processMove(direction) {
        let moved = false;
        let explosions = [];

        if (direction === 'right') {
            for (let i = 0; i < 5; i++) {
                const oldVals = [...this.board[i]];
                const oldBombs = [...this.bombBoard[i]];
                const oldRandoms = [...this.randomBoard[i]];
                const result = this.processLineWithBombs(this.getRow(i), true);
                const changed = !this.arraysEqual(oldVals, result.newLine) ||
                    !this.arraysEqual(oldBombs, result.newBombs) ||
                    !this.arraysEqual(oldRandoms, result.newRandoms);
                if (changed) moved = true;
                explosions.push(...result.explosions.map(pos => ({ row: i, col: pos })));
                this.setRow(i, result.newLine, result.newBombs, result.newRandoms, result.newSkulls, result.newTeleports);
            }
        } else if (direction === 'left') {
            for (let i = 0; i < 5; i++) {
                const oldVals = [...this.board[i]];
                const oldBombs = [...this.bombBoard[i]];
                const oldRandoms = [...this.randomBoard[i]];
                const result = this.processLineWithBombs(this.getRow(i), false);
                const changed = !this.arraysEqual(oldVals, result.newLine) ||
                    !this.arraysEqual(oldBombs, result.newBombs) ||
                    !this.arraysEqual(oldRandoms, result.newRandoms);
                if (changed) moved = true;
                explosions.push(...result.explosions.map(pos => ({ row: i, col: pos })));
                this.setRow(i, result.newLine, result.newBombs, result.newRandoms, result.newSkulls, result.newTeleports);
            }
        }
        else if (direction === 'up') {
            for (let j = 0; j < 5; j++) {
                const oldVals = [this.board[0][j], this.board[1][j], this.board[2][j], this.board[3][j], this.board[4][j]];
                const oldBombs = [this.bombBoard[0][j], this.bombBoard[1][j], this.bombBoard[2][j], this.bombBoard[3][j], this.bombBoard[4][j]];
                const oldRandoms = [this.randomBoard[0][j], this.randomBoard[1][j], this.randomBoard[2][j], this.randomBoard[3][j], this.randomBoard[4][j]];

                const result = this.processLineWithBombs(this.getColumn(j), false);
                const changed = !this.arraysEqual(oldVals, result.newLine) ||
                    !this.arraysEqual(oldBombs, result.newBombs) ||
                    !this.arraysEqual(oldRandoms, result.newRandoms);
                if (changed) moved = true;

                explosions.push(...result.explosions.map(pos => ({ row: pos, col: j })));

                this.setColumn(j, result.newLine, result.newBombs, result.newRandoms, result.newSkulls, result.newTeleports);
            }
        } else if (direction === 'down') {
            for (let j = 0; j < 5; j++) {
                const oldVals = [this.board[0][j], this.board[1][j], this.board[2][j], this.board[3][j], this.board[4][j]];
                const oldBombs = [this.bombBoard[0][j], this.bombBoard[1][j], this.bombBoard[2][j], this.bombBoard[3][j], this.bombBoard[4][j]];
                const oldRandoms = [this.randomBoard[0][j], this.randomBoard[1][j], this.randomBoard[2][j], this.randomBoard[3][j], this.randomBoard[4][j]];

                const result = this.processLineWithBombs(this.getColumn(j), true);
                const changed = !this.arraysEqual(oldVals, result.newLine) ||
                    !this.arraysEqual(oldBombs, result.newBombs) ||
                    !this.arraysEqual(oldRandoms, result.newRandoms);
                if (changed) moved = true;

                explosions.push(...result.explosions.map(pos => ({ row: pos, col: j })));

                this.setColumn(j, result.newLine, result.newBombs, result.newRandoms, result.newSkulls, result.newTeleports);
            }
        }
        // 僅當確實有移動或合併時才處理爆炸與後續邏輯
        if (explosions.length > 0) this.handleExplosions(explosions);
        return moved;
    }

    getRow(i) {
        const line = [];
        for (let j = 0; j < 5; j++) {
            const tile = this.board[i][j];
            if (tile.value !== 0) {
                line.push({
                    value: tile.value,
                    isBomb: tile.type === TILE_TYPES.BOMB,
                    isRandom: tile.type === TILE_TYPES.RANDOM,
                    isHeart: tile.type === TILE_TYPES.SKULL,
                    isTeleport: tile.type === TILE_TYPES.TELEPORT
                });
            }
        }

        return line;
    }

    getColumn(j) {
        const line = [];
        for (let i = 0; i < 5; i++) {
            const tile = this.board[i][j];
            if (tile.value !== 0) {
                line.push({
                    value: tile.value,
                    isBomb: tile.type === TILE_TYPES.BOMB,
                    isRandom: tile.type === TILE_TYPES.RANDOM,
                    isHeart: tile.type === TILE_TYPES.SKULL,
                    isTeleport: tile.type === TILE_TYPES.TELEPORT
                });
            }
        }
        return line;
    }

    arraysEqual(a, b) {
        if (!a || !b || a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
        return true;
    }

    setRow(i, newLine, newBombs, newRandoms, newSkulls, newTeleports) {
        for (let j = 0; j < 5; j++) {
            const value = newLine[j] || 0;
            let tileType = TILE_TYPES.NORMAL;

            if (newBombs[j]) tileType = TILE_TYPES.BOMB;
            else if (newRandoms[j]) tileType = TILE_TYPES.RANDOM;
            else if (newSkulls[j]) tileType = TILE_TYPES.SKULL;
            else if (newTeleports[j]) tileType = TILE_TYPES.TELEPORT;

            this.board[i][j] = new Tile(value, tileType);

            // 同步到舊陣列
            this.bombBoard[i][j] = newBombs[j] || false;
            this.randomBoard[i][j] = newRandoms[j] || false;
            this.skullBoard[i][j] = newSkulls[j] || false;
            this.teleportBoard[i][j] = newTeleports[j] || false;
        }
    }

    setColumn(j, newLine, newBombs, newRandoms, newSkulls, newTeleports) {
        for (let i = 0; i < 5; i++) {
            const value = newLine[i] || 0;
            let tileType = TILE_TYPES.NORMAL;

            if (newBombs[i]) tileType = TILE_TYPES.BOMB;
            else if (newRandoms[i]) tileType = TILE_TYPES.RANDOM;
            else if (newSkulls[i]) tileType = TILE_TYPES.SKULL;
            else if (newTeleports[i]) tileType = TILE_TYPES.TELEPORT;

            this.board[i][j] = new Tile(value, tileType);

            // 同步到舊陣列
            this.bombBoard[i][j] = newBombs[i] || false;
            this.randomBoard[i][j] = newRandoms[i] || false;
            this.skullBoard[i][j] = newSkulls[i] || false;
            this.teleportBoard[i][j] = newTeleports[i] || false;
        }
    }

    processLineWithBombs(line, reverse) {
        if (reverse) line.reverse();
        const explosions = [];
        for (let i = 0; i < line.length - 1; i++) {
            // 愛心方塊不能被合併
            if (line[i].isHeart || line[i + 1].isHeart) {
                continue;
            }

            // 傳送方塊邏輯：碰到任何方塊時，該方塊消失並傳送給對手
            if (line[i].isTeleport || line[i + 1].isTeleport) {
                // 如果 i 是傳送方塊，碰到 i+1
                if (line[i].isTeleport) {
                    // 移除兩個方塊（i+1 傳送給對手，傳送方塊本身也消失）
                    line.splice(i, 2);
                    line.moved = true;
                    i--; // 調整索引
                    continue;
                }
                // 如果 i+1 是傳送方塊，碰到 i
                else if (line[i + 1].isTeleport) {
                    // 移除兩個方塊（i 傳送給對手，傳送方塊本身也消失）
                    line.splice(i, 2);
                    line.moved = true;
                    i--; // 調整索引
                    continue;
                }
            }

            if (line[i].value === line[i + 1].value && line[i].value < this.MAX_VALUE && !line[i].isRandom && !line[i + 1].isRandom) {

                if (line[i].isBomb || line[i + 1].isBomb) {
                    // 僅當這一碰撞真的發生（值相等且都不是隨機）才記錄爆炸
                    explosions.push(i);
                }

                const newValue = line[i].value + line[i + 1].value;
                line[i] = { value: Math.min(newValue, this.MAX_VALUE), isBomb: false, isRandom: false, isHeart: false, isTeleport: false };
                line.splice(i + 1, 1);
                this.score += line[i].value * this.VALUE_MULTIPLIER;
                // 標記為已移動（或合併）
                line.moved = true;
            }
        }
        while (line.length < 5) {
            line.push({ value: 0, isBomb: false, isRandom: false, isHeart: false, isTeleport: false });
        }
        if (reverse) {
            line.reverse();
            explosions.forEach((pos, index) => { explosions[index] = 4 - pos; });
        }
        return {
            newLine: line.map(item => item.value),
            newBombs: line.map(item => item.isBomb),
            newRandoms: line.map(item => item.isRandom),
            newSkulls: line.map(item => item.isHeart),
            newTeleports: line.map(item => item.isTeleport),
            explosions: explosions,
            moved: !!line.moved
        };
    }

    /**
     * 【Canvas 初始化】- 創建 Canvas 畫布並設置尺寸
     */
    initCanvas() {
        const gameBoard = document.getElementById('gameBoard');
        if (!gameBoard) {
            setTimeout(() => this.initCanvas(), 100);
            return;
        }

        // 創建 Canvas 元素
        this.canvas = document.createElement('canvas');
        this.canvas.id = 'gameCanvas';
        this.ctx = this.canvas.getContext('2d');

        // 設置 Canvas 尺寸（響應式）
        this.resizeCanvas();

        // 清空原有內容並添加 Canvas
        gameBoard.innerHTML = '';
        gameBoard.appendChild(this.canvas);

        // 添加點擊事件監聽器（用於隨機方塊確認）
        this.canvas.addEventListener('click', (e) => this.handleCanvasClick(e));

        // ✅ 添加觸控事件監聽器（手機版支援）
        this.setupCanvasTouchControls();

        // 監聽窗口大小變化
        window.addEventListener('resize', () => this.resizeCanvas());
    }

    /**
     * 【Canvas 尺寸調整】- 根據容器大小調整 Canvas
     */
    resizeCanvas() {
        if (!this.canvas) return;

        const gameBoard = document.getElementById('gameBoard');
        if (!gameBoard) return;

        const boardSize = Math.min(gameBoard.clientWidth, gameBoard.clientHeight, 500);
        this.canvas.width = boardSize;
        this.canvas.height = boardSize;

        // 計算每個格子的大小
        this.cellSize = (boardSize - this.cellPadding * 6) / 5;

        // 重新渲染
        this.renderBoard();
    }

    /**
     * 【Canvas 點擊處理】- 處理玩家點擊事件
     */
    handleCanvasClick(e) {
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // 計算點擊的格子位置
        const col = Math.floor(x / (this.cellSize + this.cellPadding));
        const row = Math.floor(y / (this.cellSize + this.cellPadding));

        if (row >= 0 && row < 5 && col >= 0 && col < 5) {
            // 檢查是否點擊了隨機方塊
            if (this.randomBoard[row][col]) {
                this.confirmRandomTile(row, col);
            }
        }
    }

    /**
     * 【Canvas 觸控控制】- 處理手機觸控事件（包含雙擊檢測）
     */
    setupCanvasTouchControls() {
        if (!this.canvas) return;

        let lastTouchTime = 0;
        let lastTouchRow = -1;
        let lastTouchCol = -1;
        const doubleTapDelay = 200; // 雙擊間隔時間（毫秒）

        this.canvas.addEventListener('touchend', (e) => {
            e.preventDefault();

            const touch = e.changedTouches[0];
            const rect = this.canvas.getBoundingClientRect();
            const x = touch.clientX - rect.left;
            const y = touch.clientY - rect.top;
            const currentTime = Date.now();

            // 計算觸控的格子位置
            const col = Math.floor(x / (this.cellSize + this.cellPadding));
            const row = Math.floor(y / (this.cellSize + this.cellPadding));

            if (row >= 0 && row < 5 && col >= 0 && col < 5) {
                // 檢查是否觸控了隨機方塊
                if (this.randomBoard[row][col]) {
                    const timeSinceLastTouch = currentTime - lastTouchTime;
                    const isSameCell = (row === lastTouchRow && col === lastTouchCol);

                    // 檢查是否為雙擊
                    if (isSameCell && timeSinceLastTouch < doubleTapDelay) {
                        this.confirmRandomTile(row, col);
                        // 重置雙擊檢測
                        lastTouchTime = 0;
                        lastTouchRow = -1;
                        lastTouchCol = -1;
                    } else {
                        // 記錄第一次點擊
                        lastTouchTime = currentTime;
                        lastTouchRow = row;
                        lastTouchCol = col;
                    }
                }
            }
        }, { passive: false });
    }

    /**
     * 【取得方塊顏色】- 根據數值返回對應的顏色（參考圖片風格）
     */
    getTileColor(value) {
        const colors = {
            0: 'rgba(238, 228, 218, 0.35)',
            1: '#eee4da',
            2: '#eee4da',
            4: '#ede0c8',
            8: '#f2b179',
            16: '#f59563',
            32: '#f67c5f',
            64: '#f65e3b',
            128: '#edcc61',
            256: '#edc850',
            512: '#edc53f',
            1024: '#edc22e',
            2048: '#F7931A'
        };
        return colors[value] || '#3c3a32';
    }

    /**
     * 【取得文字顏色】- 根據數值返回對應的文字顏色
     */
    getTextColor(value) {
        return value <= 4 ? '#776e65' : '#f9f6f2';
    }

    /**
     * 【繪製方塊】- 在 Canvas 上繪製單個方塊（參考圖片風格）
     */
    drawTile(row, col, value, scale = 1, isBomb = false, isRandom = false, isHeart = false, isTeleport = false, offsetX = 0, offsetY = 0) {
        const x = col * (this.cellSize + this.cellPadding) + this.cellPadding + offsetX;
        const y = row * (this.cellSize + this.cellPadding) + this.cellPadding + offsetY;
        const size = this.cellSize * scale;
        const offset = (this.cellSize - size) / 2;
        const radius = 8; // 更大的圓角

        // 繪製圓角矩形方塊
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.moveTo(x + offset + radius, y + offset);
        this.ctx.lineTo(x + offset + size - radius, y + offset);
        this.ctx.quadraticCurveTo(x + offset + size, y + offset, x + offset + size, y + offset + radius);
        this.ctx.lineTo(x + offset + size, y + offset + size - radius);
        this.ctx.quadraticCurveTo(x + offset + size, y + offset + size, x + offset + size - radius, y + offset + size);
        this.ctx.lineTo(x + offset + radius, y + offset + size);
        this.ctx.quadraticCurveTo(x + offset, y + offset + size, x + offset, y + offset + size - radius);
        this.ctx.lineTo(x + offset, y + offset + radius);
        this.ctx.quadraticCurveTo(x + offset, y + offset, x + offset + radius, y + offset);
        this.ctx.closePath();

        // 填充顏色 - 骷髏方塊使用特殊的危險色
        if (isHeart) {
            // 深鐵灰色，帶有輕微的紅色調，營造危險感
            this.ctx.fillStyle = '#4a5568';  // 鐵灰色
            // 或者使用其他危險色選項：
            // this.ctx.fillStyle = '#5a4a4a';  // 暗紅灰
            // this.ctx.fillStyle = '#3d3d3d';  // 深灰
            // this.ctx.fillStyle = '#6b5b5b';  // 煙灰
        } else {
            this.ctx.fillStyle = this.getTileColor(value);
        }
        this.ctx.fill();

        // 添加輕微陰影效果（只對有值的方塊）
        if (value > 0) {
            this.ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
            this.ctx.shadowBlur = 3;
            this.ctx.shadowOffsetX = 0;
            this.ctx.shadowOffsetY = 2;
            this.ctx.fill();
            this.ctx.shadowColor = 'transparent';
            this.ctx.shadowBlur = 0;
            this.ctx.shadowOffsetX = 0;
            this.ctx.shadowOffsetY = 0;
        }

        this.ctx.restore();

        // 金色方塊邊框 (value >= 128) - 增強版閃亮效果
        if (value >= 128) {
            // 使用時間創建動態閃爍效果
            const time = Date.now() / 1000; // 轉換為秒
            const shimmer = Math.sin(time * 2) * 0.5 + 0.5; // 0-1 之間振盪

            // 第一層：外層強烈光暈
            this.ctx.save();
            this.ctx.shadowColor = `rgba(255, 215, 0, ${0.6 + shimmer * 0.4})`;
            this.ctx.shadowBlur = 25;
            this.ctx.strokeStyle = '#FFD700';
            this.ctx.lineWidth = 6;
            this.ctx.stroke();
            this.ctx.restore();

            // 第二層：中層光暈
            this.ctx.save();
            this.ctx.shadowColor = `rgba(255, 165, 0, ${0.7 + shimmer * 0.3})`;
            this.ctx.shadowBlur = 15;
            this.ctx.strokeStyle = '#FFA500';
            this.ctx.lineWidth = 5;
            this.ctx.stroke();
            this.ctx.restore();

            // 第三層：內層金色邊框（帶動態漸變）
            const angle = time * 0.5; // 旋轉角度
            const centerX = x + offset + size / 2;
            const centerY = y + offset + size / 2;
            const radius = size * 0.7;

            const gradient = this.ctx.createLinearGradient(
                centerX + Math.cos(angle) * radius,
                centerY + Math.sin(angle) * radius,
                centerX - Math.cos(angle) * radius,
                centerY - Math.sin(angle) * radius
            );
            gradient.addColorStop(0, '#FFED4E');    // 亮金色
            gradient.addColorStop(0.3, '#FFD700');  // 金色
            gradient.addColorStop(0.5, '#FFA500');  // 橙金色
            gradient.addColorStop(0.7, '#FFD700');  // 金色
            gradient.addColorStop(1, '#FFED4E');    // 亮金色

            this.ctx.save();
            this.ctx.strokeStyle = gradient;
            this.ctx.lineWidth = 4;
            this.ctx.shadowColor = `rgba(255, 255, 255, ${0.5 + shimmer * 0.5})`;
            this.ctx.shadowBlur = 8;
            this.ctx.stroke();
            this.ctx.restore();

            // 第四層：白色高光閃爍
            if (shimmer > 0.7) {
                this.ctx.save();
                this.ctx.strokeStyle = `rgba(255, 255, 255, ${(shimmer - 0.7) * 2})`;
                this.ctx.lineWidth = 2;
                this.ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
                this.ctx.shadowBlur = 12;
                this.ctx.stroke();
                this.ctx.restore();
            }
        }

        if (value > 0) {
            this.ctx.fillStyle = this.getTextColor(value);
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';

            if (isRandom) {
                this.ctx.font = `bold ${size * 0.5}px "Clear Sans", "Helvetica Neue", Arial, sans-serif`;
                this.ctx.fillText('？', x + this.cellSize / 2, y + this.cellSize / 2);
            } else if (isHeart) {
                this.ctx.font = `${size * 0.6}px Arial`;
                this.ctx.fillText('💀', x + this.cellSize / 2, y + this.cellSize / 2);
            } else if (isTeleport) {
                this.ctx.font = `${size * 0.5}px Arial`;
                this.ctx.fillText('🌀', x + this.cellSize / 2, y + this.cellSize / 2);
            } else {
                const fontSize = value >= 100 ? size * 0.35 : value >= 1000 ? size * 0.3 : size * 0.5;
                this.ctx.font = `bold ${fontSize}px "Clear Sans", "Helvetica Neue", Arial, sans-serif`;
                this.ctx.fillText(String(value), x + this.cellSize / 2, y + this.cellSize / 2);
            }

            // 如果是炸彈方塊，繪製炸彈圖標
            if (isBomb) {
                this.ctx.font = `${size * 0.25}px Arial`;
                this.ctx.fillText('💣', x + offset + size * 0.85, y + offset + size * 0.2);
            }
        }
    }

    /**
     * 【繪製背景網格】- 繪製空的遊戲棋盤背景（參考圖片風格）
     */
    drawBackground() {
        if (!this.canvas || !this.ctx) return;

        // 清空畫布
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // 繪製背景（深褐色）
        this.ctx.fillStyle = '#8f7a66';

        // 繪製圓角背景
        const radius = 6;
        this.ctx.beginPath();
        this.ctx.moveTo(radius, 0);
        this.ctx.lineTo(this.canvas.width - radius, 0);
        this.ctx.quadraticCurveTo(this.canvas.width, 0, this.canvas.width, radius);
        this.ctx.lineTo(this.canvas.width, this.canvas.height - radius);
        this.ctx.quadraticCurveTo(this.canvas.width, this.canvas.height, this.canvas.width - radius, this.canvas.height);
        this.ctx.lineTo(radius, this.canvas.height);
        this.ctx.quadraticCurveTo(0, this.canvas.height, 0, this.canvas.height - radius);
        this.ctx.lineTo(0, radius);
        this.ctx.quadraticCurveTo(0, 0, radius, 0);
        this.ctx.closePath();
        this.ctx.fill();

        // 繪製空格子（圓角矩形）
        for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
                const x = j * (this.cellSize + this.cellPadding) + this.cellPadding;
                const y = i * (this.cellSize + this.cellPadding) + this.cellPadding;
                const cellRadius = 5;

                this.ctx.fillStyle = 'rgba(238, 228, 218, 0.35)';
                this.ctx.beginPath();
                this.ctx.moveTo(x + cellRadius, y);
                this.ctx.lineTo(x + this.cellSize - cellRadius, y);
                this.ctx.quadraticCurveTo(x + this.cellSize, y, x + this.cellSize, y + cellRadius);
                this.ctx.lineTo(x + this.cellSize, y + this.cellSize - cellRadius);
                this.ctx.quadraticCurveTo(x + this.cellSize, y + this.cellSize, x + this.cellSize - cellRadius, y + this.cellSize);
                this.ctx.lineTo(x + cellRadius, y + this.cellSize);
                this.ctx.quadraticCurveTo(x, y + this.cellSize, x, y + this.cellSize - cellRadius);
                this.ctx.lineTo(x, y + cellRadius);
                this.ctx.quadraticCurveTo(x, y, x + cellRadius, y);
                this.ctx.closePath();
                this.ctx.fill();
            }
        }
    }

    /**
     * 【動畫循環】- 使用 requestAnimationFrame 持續更新畫面
     */
    startAnimationLoop() {
        const animate = () => {
            // ✅ 確保 Canvas 已初始化
            if (!this.canvas || !this.ctx) {
                // 如果 Canvas 還沒準備好，繼續等待
                this.animationId = requestAnimationFrame(animate);
                return;
            }

            // 更新所有動畫
            this.updateAnimations();

            // 繪製畫面
            this.drawBackground();
            this.drawExplosionWaves(); // 先繪製爆炸波（在方塊下方）
            this.drawAllTiles();
            this.drawParticleEffects();

            // 繼續循環
            this.animationId = requestAnimationFrame(animate);
        };

        animate();
    }

    /**
     * 【繪製爆炸波】- 繪製所有爆炸波動畫
     */
    drawExplosionWaves() {
        if (!this.canvas || !this.ctx) return;

        this.tileAnimations.filter(anim => anim.type === 'explosion_wave').forEach(wave => {
            this.ctx.save();
            this.ctx.globalAlpha = wave.alpha;
            this.ctx.strokeStyle = '#ff6b00';
            this.ctx.lineWidth = 5;
            this.ctx.beginPath();
            this.ctx.arc(wave.x, wave.y, wave.radius, 0, Math.PI * 2);
            this.ctx.stroke();

            // 內圈
            this.ctx.strokeStyle = '#ffa500';
            this.ctx.lineWidth = 3;
            this.ctx.beginPath();
            this.ctx.arc(wave.x, wave.y, wave.radius * 0.7, 0, Math.PI * 2);
            this.ctx.stroke();
            this.ctx.restore();
        });
    }

    /**
     * 【更新動畫】- 更新所有進行中的動畫
     */
    updateAnimations() {
        // 更新方塊動畫
        this.tileAnimations = this.tileAnimations.filter(anim => {
            if (anim.type === 'explosion_wave') {
                anim.progress += 0.05;
                anim.radius = anim.maxRadius * anim.progress;
                anim.alpha = 1 - anim.progress;
                return anim.progress < 1;
            } else if (anim.type === 'appear') {
                // 平滑出現動畫 - 讓新方塊慢慢彈出，更容易看見
                anim.progress += 0.08; // 約 12-13 幀完成 (~200ms)
                return anim.progress < 1;
            } else {
                anim.progress += 0.1;
                return anim.progress < 1;
            }
        });

        // 更新粒子效果
        this.particleEffects = this.particleEffects.filter(particle => {
            particle.life -= 0.02;
            particle.x += particle.vx;
            particle.y += particle.vy;
            particle.vy += 0.5; // 重力效果
            return particle.life > 0;
        });
    }

    /**
     * 【繪製所有方塊】- 繪製棋盤上的所有方塊，支援移動動畫
     */
    drawAllTiles() {
        if (!this.canvas || !this.ctx) return;

        const currentTime = Date.now();
        const MOVE_DURATION = this.speedConfig.moveDuration; // 使用對外統一的配置

        for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
                const tile = this.board[i][j];
                const value = tile.value;

                if (value > 0) {
                    const isBomb = tile.type === TILE_TYPES.BOMB;
                    const isRandom = tile.type === TILE_TYPES.RANDOM;
                    const isHeart = tile.type === TILE_TYPES.SKULL;
                    const isTeleport = tile.type === TILE_TYPES.TELEPORT;

                    // 查找是否有縮放動畫（方塊出現動畫）
                    const anim = this.tileAnimations.find(a => a.row === i && a.col === j);
                    let scale = 1;
                    if (anim && anim.type === 'appear') {
                        // 使用從 0 到 1 的平滑過渡，帶一點彈跳效果
                        const progress = anim.progress;
                        // 先快速放大到 1.1，然後回彈到 1.0
                        if (progress < 0.7) {
                            scale = progress / 0.7 * 1.15; // 0 -> 1.15
                        } else {
                            const bounce = (progress - 0.7) / 0.3;
                            scale = 1.15 - bounce * 0.15; // 1.15 -> 1.0
                        }
                    }

                    // 檢查是否有移動動畫
                    const tileKey = `${i}-${j}`;
                    const posData = this.tilePositions[tileKey];

                    let offsetX = 0;
                    let offsetY = 0;

                    if (posData && posData.startTime && currentTime - posData.startTime < MOVE_DURATION) {
                        // 計算移動進度 (0 to 1)
                        const progress = (currentTime - posData.startTime) / MOVE_DURATION;
                        // 使用彈性緩動函數，產生到位時的彈動效果
                        const easedProgress = this.easeOutBack(progress);

                        // 計算當前偏移量
                        offsetX = posData.offsetX * (1 - easedProgress);
                        offsetY = posData.offsetY * (1 - easedProgress);
                    }

                    this.drawTile(i, j, value, scale, isBomb, isRandom, isHeart, isTeleport, offsetX, offsetY);
                }
            }
        }
    }

    /**
     * 【繪製粒子效果】- 繪製所有粒子
     */
    drawParticleEffects() {
        if (!this.canvas || !this.ctx) return;

        this.particleEffects.forEach(particle => {
            this.ctx.save();
            this.ctx.globalAlpha = particle.life;
            this.ctx.fillStyle = particle.color;
            this.ctx.fillRect(particle.x, particle.y, particle.size, particle.size);
            this.ctx.restore();
        });
    }

    /**
     * 【彈性緩動函數】- 讓動畫更有彈性
     */
    easeOutElastic(x) {
        const c4 = (2 * Math.PI) / 3;
        return x === 0 ? 0 : x === 1 ? 1 : Math.pow(2, -10 * x) * Math.sin((x * 10 - 0.75) * c4) + 1;
    }

    /**
     * 【三次緩動函數】- 用於平滑移動動畫
     */
    easeOutCubic(x) {
        return 1 - Math.pow(1 - x, 3);
    }

    /**
     * 【回彈緩動函數】- 產生到位時的彈動效果（超出後彈回）
     */
    easeOutBack(x) {
        const c1 = 1.70158; // 控制回彈幅度，數值越大彈動越明顯
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
    }

    /**
     * 【保存棋盤狀態】- 在移動前保存當前狀態
     */
    saveBoardState() {
        this.previousBoardState = {
            board: this.board.map(row => row.map(tile => tile.clone())),  // 深拷貝 Tile 物件
            bombBoard: this.bombBoard.map(row => [...row]),
            randomBoard: this.randomBoard.map(row => [...row]),
            skullBoard: this.skullBoard.map(row => [...row]),
            teleportBoard: this.teleportBoard.map(row => [...row])
        };
    }

    /**
     * 【計算方塊移動】- 比較新舊棋盤狀態，計算方塊移動偏移量
     */
    calculateTileMovements(direction) {
        if (!this.previousBoardState) return;

        const oldBoard = this.previousBoardState.board;
        const newBoard = this.board;
        const currentTime = Date.now();

        // 清空之前的位置數據
        this.tilePositions = {};

        // 對於每個新位置的方塊，找出它從哪裡來
        for (let newRow = 0; newRow < 5; newRow++) {
            for (let newCol = 0; newCol < 5; newCol++) {
                const newTile = newBoard[newRow][newCol];
                const value = newTile.value;
                if (value === 0) continue;

                // 根據移動方向計算可能的舊位置
                let oldRow = newRow;
                let oldCol = newCol;

                switch (direction) {
                    case 'up':
                        // 方塊向上移動，所以舊位置在下方
                        for (let r = newRow + 1; r < 5; r++) {
                            if (oldBoard[r][newCol].value === value) {
                                oldRow = r;
                                break;
                            }
                        }
                        break;
                    case 'down':
                        // 方塊向下移動，所以舊位置在上方
                        for (let r = newRow - 1; r >= 0; r--) {
                            if (oldBoard[r][newCol].value === value) {
                                oldRow = r;
                                break;
                            }
                        }
                        break;
                    case 'left':
                        // 方塊向左移動，所以舊位置在右方
                        for (let c = newCol + 1; c < 5; c++) {
                            if (oldBoard[newRow][c].value === value) {
                                oldCol = c;
                                break;
                            }
                        }
                        break;
                    case 'right':
                        // 方塊向右移動，所以舊位置在左方
                        for (let c = newCol - 1; c >= 0; c--) {
                            if (oldBoard[newRow][c].value === value) {
                                oldCol = c;
                                break;
                            }
                        }
                        break;
                }

                // 計算像素偏移量
                const offsetX = (oldCol - newCol) * (this.cellSize + this.cellPadding);
                const offsetY = (oldRow - newRow) * (this.cellSize + this.cellPadding);

                // 只有當有實際移動時才記錄
                if (offsetX !== 0 || offsetY !== 0) {
                    const tileKey = `${newRow}-${newCol}`;
                    this.tilePositions[tileKey] = {
                        offsetX,
                        offsetY,
                        startTime: currentTime
                    };
                }
            }
        }
    }

    /**
     * 【添加方塊出現動畫】- 新方塊出現時的動畫效果
     */
    addTileAppearAnimation(row, col) {
        this.tileAnimations.push({
            row,
            col,
            type: 'appear',
            progress: 0
        });
    }

    /**
     * 【添加爆炸效果】- 創建爆炸粒子效果
     */
    addExplosionEffect(row, col) {
        const x = col * (this.cellSize + this.cellPadding) + this.cellPadding + this.cellSize / 2;
        const y = row * (this.cellSize + this.cellPadding) + this.cellPadding + this.cellSize / 2;

        // 創建粒子
        for (let i = 0; i < 20; i++) {
            const angle = (Math.PI * 2 * i) / 20;
            const speed = 3 + Math.random() * 3;
            this.particleEffects.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                life: 1,
                size: 5 + Math.random() * 5,
                color: `hsl(${Math.random() * 60 + 10}, 100%, 50%)`
            });
        }
    }

    async addRandomTileSignalR() {

        let responseOut;
        let randomCell = null;
        let isRandom = false;
        let isHeart = false;
        let isBomb = false;
        let isTeleport = false;
        let teleportValue = 0;
        let bombValue = 0;
        let tileValue = 0;

        // 找出所有空位（數值為0的格子）- 使用 Tile 物件
        const emptyCells = [];
        for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
                if (this.board[i][j].value === 0) emptyCells.push({ i, j });
            }
        }

        if (emptyCells.length > 0) {

            try {
                const response = await this.sendPlayerActionSignalR({ type: 'add_random_tile', gameId: sessionStorage.getItem('gameId') });
                // const response = await this.waitForServerResponse('add_random_tile');
                responseOut = response.data;
                randomCell = response.data.tile;
                isRandom = response.data.isRandom;
                isHeart = response.data.isHeart;
                isBomb = response.data.isBomb;
                bombValue = response.data.bombValue;
                tileValue = response.data.tileValue;
                isTeleport = response.data.isTeleport;
                teleportValue = response.data.teleportValue;
            } catch (error) {
                console.error('❌ 添加隨機方塊失敗:', error);
            }

            // 使用統一方法設置方塊
            if (isRandom) {
                this.setTileAtPosition(randomCell.i, randomCell.j, 'random', 1, '生成');
            } else if (isHeart) {
                this.setTileAtPosition(randomCell.i, randomCell.j, 'heart', 1, '生成');
            } else if (isBomb) {
                this.setTileAtPosition(randomCell.i, randomCell.j, 'bomb', bombValue, '生成');
            } else if (isTeleport) {
                this.setTileAtPosition(randomCell.i, randomCell.j, 'teleport', 1, '生成');
            } else {
                this.setTileAtPosition(randomCell.i, randomCell.j, 'normal', tileValue, '生成');
            }

            // 生成後清理無效狀態，確保空格子沒有殘留的特殊標記
            this.sanitizeBoards();

            // 強制渲染以確保畫面更新
            this.renderBoard();

            return true; // 成功添加方塊
        }

        return false; // 沒有空位，無法添加方塊
    }

    renderBoard(animateNew = false) {
        // ✅ 增強的 Canvas 檢查
        if (!this.canvas || !this.ctx) {
            console.warn('⚠️ Canvas 尚未初始化，延遲渲染...');
            // 延遲一小段時間後重試
            setTimeout(() => this.renderBoard(animateNew), 50);
            return;
        }

        // 每次渲染前做狀態清理
        this.sanitizeBoards();

        // 如果需要新方塊動畫
        if (animateNew) {
            // ✅ 如果沒有 previousBoard，先創建一個空的
            if (!this.previousBoard) {
                this.previousBoard = Array(5).fill(null).map(() =>
                    Array(5).fill(null).map(() => new Tile(0, TILE_TYPES.NORMAL))
                );
            }

            for (let i = 0; i < 5; i++) {
                for (let j = 0; j < 5; j++) {
                    // 檢測新出現的方塊 - 比較 Tile 的 value
                    const prevValue = this.previousBoard[i][j]?.value || 0;
                    const currValue = this.board[i][j]?.value || 0;
                    if (prevValue === 0 && currValue !== 0) {
                        this.addTileAppearAnimation(i, j);
                    }
                }
            }
        }

        // ✅ 渲染後保存當前狀態，用於下次比較 - 深拷貝 Tile 物件
        // ✅ 安全檢查：確保所有 tile 都是 Tile 實例
        this.previousBoard = this.board.map((row, i) =>
            row.map((tile, j) => {
                // 如果 tile 不是 Tile 實例或沒有 clone 方法，創建新的 Tile
                if (!tile || typeof tile.clone !== 'function') {
                    console.warn(`⚠️ 位置 [${i}][${j}] 的 tile 不是有效的 Tile 實例，正在修復...`);
                    const value = tile?.value || 0;
                    const type = tile?.type !== undefined ? tile.type : TILE_TYPES.NORMAL;
                    const randomDisplay = tile?.randomDisplay || 0;
                    // 同時修復原始 board
                    this.board[i][j] = new Tile(value, type, randomDisplay);
                    return this.board[i][j].clone();
                }
                return tile.clone();
            })
        );

        // Canvas 版本不需要立即繪製，因為動畫循環會自動更新
        // 但我們可以觸發一次立即繪製以確保更新
        this.drawBackground();
        this.drawAllTiles();
    }

    updateDisplay() {
        const scoreEl = document.getElementById('score');
        const movesEl = document.getElementById('moves');
        const bestScoreEl = document.getElementById('bestScore');
        if (scoreEl) scoreEl.textContent = this.score;
        if (movesEl) movesEl.textContent = this.moves;
        if (bestScoreEl) bestScoreEl.textContent = this.bestScore;
        if (this.score > this.bestScore) {
            this.bestScore = this.score;
            localStorage.setItem('game20BestScore', this.bestScore.toString());
            if (bestScoreEl) bestScoreEl.textContent = this.bestScore;
        }
    }

    saveState() {
        this.previousState = {
            board: this.cloneBoard(), // 使用 cloneBoard 深拷貝 Tile 物件
            bombBoard: this.bombBoard.map(row => [...row]),
            score: this.score,
            moves: this.moves
        };
    }

    undoMove() {
        if (this.previousState) {
            this.board = this.previousState.board;
            this.bombBoard = this.previousState.bombBoard;
            this.score = this.previousState.score;
            this.moves = this.previousState.moves;
            this.previousState = null;

            // 同步到舊陣列
            this.syncLegacyArrays();

            this.updateDisplay();
            this.renderBoard();
        }
    }

    // ✅ 從服務器恢復遊戲狀態（用於頁面刷新後恢復）
    async restoreGameStateFromServer(gameId) {
        try {
            if (!this.signalRConnection) {
                console.warn('⚠️ SignalR 未連接，無法恢復遊戲狀態');
                return false;
            }

            const response = await this.sendPlayerActionSignalR({
                type: 'get_game_state',
                gameId: gameId
            });

            // 如果響應是字符串，嘗試解析為 JSON
            let gameState = response;
            if (typeof response === 'string') {
                try {
                    gameState = JSON.parse(response);
                } catch (e) {
                    return false;
                }
            }

            // 檢查響應類型
            if (gameState.type === 'get_game_state_response' && !gameState.success) {
                return false;
            }

            if (!gameState.success) {
                return false;
            }

            // 恢復棋盤數據
            const tiles = gameState.tiles || [];
            const bombBoard = gameState.bombBoard || [];
            const randomBoard = gameState.randomBoard || [];
            const skullBoard = gameState.heartBoard || gameState.skullBoard || [];  // 兼容舊的 heartBoard 命名
            const teleportBoard = gameState.teleportBoard || [];

            // 初始化棋盤（如果還沒初始化）
            if (this.board.length === 0) {
                this.initBoard();
            }

            // 恢復每個格子的狀態 - 創建 Tile 物件
            for (let i = 0; i < 5; i++) {
                for (let j = 0; j < 5; j++) {
                    const value = tiles[i]?.[j] || 0;
                    let tileType = TILE_TYPES.NORMAL;

                    // 根據伺服器返回的標記決定類型
                    if (bombBoard[i]?.[j]) tileType = TILE_TYPES.BOMB;
                    else if (randomBoard[i]?.[j]) tileType = TILE_TYPES.RANDOM;
                    else if (skullBoard[i]?.[j]) tileType = TILE_TYPES.SKULL;
                    else if (teleportBoard[i]?.[j]) tileType = TILE_TYPES.TELEPORT;

                    // 創建 Tile 物件
                    this.board[i][j] = new Tile(value, tileType);

                    // 同步到舊陣列
                    this.bombBoard[i][j] = bombBoard[i]?.[j] || false;
                    this.randomBoard[i][j] = randomBoard[i]?.[j] || false;
                    this.skullBoard[i][j] = skullBoard[i]?.[j] || false;
                    this.teleportBoard[i][j] = teleportBoard[i]?.[j] || false;
                }
            }

            // 恢復分數
            this.score = gameState.score || 0;

            // ✅ 確保 SignalR 連接已建立（如果還沒有的話）
            if (!this.signalRConnection || !this.isConnected) {
                await this.initSignalRConnection();
            }

            // ✅ 確保 gameId 已保存在 sessionStorage
            sessionStorage.setItem('gameId', gameId);

            // ✅ 檢查是否為遊戲結束狀態
            if (gameState.isGameOver) {
                console.log('🏁 恢復遊戲結束狀態');

                // 設置遊戲結束標記
                this.gameOverFlag = true;

                // 設置狀態為 GAME_OVER，防止玩家移動
                this.setState(Game20Engine.STATES.GAME_OVER);

                // 更新顯示
                this.updateDisplay();
                this.renderBoard();

                // 顯示遊戲結束畫面
                this.showGameOver(false, `遊戲已結束！最終分數：${this.score}`);

                return true;
            }

            // ✅ 設置狀態機為 IDLE，允許移動
            this.setState(Game20Engine.STATES.IDLE);

            // 更新顯示
            this.updateDisplay();
            this.renderBoard();

            return true;
        } catch (error) {
            return false;
        }
    }

    checkWin() {
        for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
                if (this.board[i][j].value === this.MAX_VALUE) return true;
            }
        }
        return false;
    }

    isGameOver() {
        // 檢查是否有空格
        for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
                if (this.board[i][j].value === 0) return false;
            }
        }

        // 檢查是否有可合併的方塊
        for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
                const tile = this.board[i][j];
                const current = tile.value;

                // 跳過愛心方塊和隨機方塊（它們不能被合併）
                if (tile.type === TILE_TYPES.SKULL || tile.type === TILE_TYPES.RANDOM) {
                    continue;
                }

                if (current < this.MAX_VALUE && current > 0) {
                    // 檢查右邊的方塊
                    if (j < 4) {
                        const rightTile = this.board[i][j + 1];
                        // 如果右邊的方塊相同且不是愛心或隨機方塊，則可以合併
                        if (rightTile.value === current &&
                            rightTile.type !== TILE_TYPES.SKULL &&
                            rightTile.type !== TILE_TYPES.RANDOM) {
                            return false;
                        }
                    }

                    // 檢查下面的方塊
                    if (i < 4) {
                        const bottomTile = this.board[i + 1][j];
                        // 如果下面的方塊相同且不是愛心或隨機方塊，則可以合併
                        if (bottomTile.value === current &&
                            bottomTile.type !== TILE_TYPES.SKULL &&
                            bottomTile.type !== TILE_TYPES.RANDOM) {
                            return false;
                        }
                    }
                }
            }
        }

        // 如果沒有空格且沒有可合併的方塊，遊戲結束
        return true;
    }

    showCelebration() {
        const result = document.querySelector('.result') || document.createElement('div');
        if (!document.querySelector('.result')) {
            result.className = 'result';
            const info = document.querySelector('.game-info');
            if (info) info.appendChild(result);
        }
        result.textContent = '🎉 太棒了！你達到了20！繼續挑戰更高分！';
        result.style.color = '#4caf50';
        result.style.fontWeight = 'bold';
        result.style.textShadow = '0 0 10px rgba(76, 175, 80, 0.5)';
        result.style.fontSize = '1.1em';
        result.style.marginTop = '10px';
        result.style.textAlign = 'center';
        setTimeout(() => { result.style.opacity = '0.7'; }, 3000);
    }

    showGameOver(isWin, customMessage = null) {
        const gameOver = document.getElementById('gameOver');
        const content = document.getElementById('gameOverContent');
        const title = document.getElementById('gameOverTitle');
        const message = document.getElementById('gameOverMessage');
        if (isWin) {
            if (content) content.classList.add('game-win');
            if (title) title.textContent = '🎉 恭喜獲勝！';
            if (message) message.textContent = `你達到了20並堅持到最後！分數：${this.score}`;
        } else {
            if (content) content.classList.remove('game-win');
            if (title) title.textContent = '😞 遊戲結束';
            if (message) message.textContent = customMessage || `無法繼續合併了！最終分數：${this.score}`;
        }
        if (gameOver) gameOver.style.display = 'flex';

        // if(this.address && this.score > 0){
        //     mintTwentyTokenApiAsync(
        //         {
        //             recipient:this.address, 
        //             amount:this.score
        //         });
        // }
    }

    /**
      * 創建新遊戲
      * @returns {Promise<{gameId: string, multiplayerGameId: string}|null>} 返回遊戲創建結果
      */
    async newGame(gameType = 0, multiplayerGameId = '', userSignalRConnection = '') {
        const gameOver = document.getElementById('gameOver');
        if (gameOver) gameOver.style.display = 'none';

        // 重置狀態機
        this.resetStateMachine();

        if (this.randomAnimationInterval) {
            clearInterval(this.randomAnimationInterval);
            this.randomAnimationInterval = null;
        }

        this.board = [];
        this.bombBoard = [];
        this.randomBoard = [];
        this.randomDisplayBoard = [];
        this.skullBoard = [];
        this.score = 0;
        this.moves = 0;
        this.hasWon = false;
        this.previousState = null;
        this.previousBoard = null;
        this.previousBombBoard = null;
        this.previousRandomBoard = null;
        this.previousskullBoard = null;

        // ✅ ETH版本：更新地址和會話ID
        this.address = sessionStorage.getItem('zklogin_user_address');
        if (!this.address) {
            this.sessionId = sessionStorage.getItem('anonymous_session_id');
            if (!this.sessionId) {
                this.sessionId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                sessionStorage.setItem('anonymous_session_id', this.sessionId);
            }
        }

        // 重新初始化棋盤
        this.initBoard();
        this.updateDisplay();

        // ✅ 等待遊戲初始化完成並返回結果
        const result = await this.initGameWithSignalR(gameType, multiplayerGameId, userSignalRConnection);

        return result;
    }

    /**
     * 【重置狀態機】- 新遊戲時重置狀態
     */
    resetStateMachine() {
        this.gameState = Game20Engine.STATES.IDLE;
        this.stateHistory = [];
        this.lastMoveTime = 0;
        this.isMoving = false;
        this.isActionLocked = false;
        this.isExploding = false;
    }

    /**
     * 【狀態機調試方法】
     */
    getStateInfo() {
        return {
            currentState: this.gameState,
            canMove: this.canMove(),
            isDebounceBlocked: this.isDebounceBlocked(),
            stateHistory: this.stateHistory.slice(-5) // 最近5個狀態
        };
    }

    setExplosionMode(mode, range = 1) {
        const validModes = ['cross', 'square'];
        if (validModes.includes(mode)) {
            this.explosionMode = mode;
            this.explosionRange = range;
        }
    }

    toggleDebugMode() {
        this.debugMode = !this.debugMode;
    }

    setRandomNumbers(numbers) {
        if (Array.isArray(numbers) && numbers.length > 0) {
            this.randomNumbers = numbers;
            console.log(`✅ 随机数可能值已设置为: [${numbers.join(', ')}]`);
        } else {
            console.error('❌ 请提供有效的数字数组，例如: [1,2,4,8,16]');
        }
    }

    testProbabilities(testCount = 1000) {
        let randomCount = 0, bomb2Count = 0, bomb4Count = 0, bomb8Count = 0, normalCount = 0;
        for (let i = 0; i < testCount; i++) {
            const random = Math.random();
            let isRandom = false;
            if (random < this.randomTileProbability) {
                randomCount++; isRandom = true;
            } else {
                const bombCheck = Math.random();
                if (bombCheck < this.bombProbabilities[2]) bomb2Count++;
                else if (bombCheck < this.bombProbabilities[2] + this.bombProbabilities[4]) bomb4Count++;
                else if (bombCheck < this.bombProbabilities[2] + this.bombProbabilities[4] + this.bombProbabilities[8]) bomb8Count++;
                else normalCount++;
            }
        }
    }

    testRandomNumbers() {
        for (let i = 0; i < 10; i++) {
            const value = this.randomNumbers[Math.floor(Math.random() * this.randomNumbers.length)];
        }
    }

    /**
     * 處理連線錯誤
     * @param {Error} error - 錯誤物件
     */
    handleConnectionError(error) {
        this.isConnected = false;
        this.hideConnectingModal();

        // 顯示錯誤訊息給用戶
        if (error && error.message) {
            alert(`連線錯誤: ${error.message}`);
        } else {
            alert('連線發生錯誤，請檢查網路連線或稍後再試');
        }
    }

    /**
     * 處理連線斷開
     * @param {Object} data - 斷開連線的資料
     */
    handleDisconnection(data) {
        this.isConnected = false;
        this.hideConnectingModal();

        // 根據斷開原因顯示不同訊息
        if (data && data.code) {
            if (data.code === 1000) {
                console.log('正常關閉連線');
            } else {
                console.warn(`連線異常斷開，代碼: ${data.code}, 原因: ${data.reason || '未知'}`);
            }
        }

        // 停止心跳檢測和 getNotSignEvents 排程
        this.stopHeartbeat();
        this.stopNotSignEventsScheduler();
    }

    /**
     * 開始心跳檢測
     */
    startHeartbeat() {
        console.log('💓 開始心跳檢測');

        // 先停止現有的心跳檢測
        this.stopHeartbeat();

        // 設定新的心跳檢測
        this.heartbeatInterval = setInterval(async () => {
            if (this.isConnected && this.signalRConnection) {
                try {
                    // 發送心跳訊息
                    await sendSuiGameAction({
                        type: 'heartbeat'
                    });
                    console.log('💓 發送心跳檢測');
                } catch (error) {
                    console.error('❌ 心跳檢測發送失敗:', error);
                    this.isConnected = false;
                }
            } else {
                console.warn('⚠️ 心跳檢測跳過：連線未建立');
                this.stopHeartbeat();
            }
        }, this.heartbeatIntervalMs);
    }

    /**
     * 停止心跳檢測
     */
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            console.log('💓 停止心跳檢測');
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    startNotSignEventsScheduler() {
        // 先停止現有的排程
        this.stopNotSignEventsScheduler();
    }

    stopNotSignEventsScheduler() {
        if (this.notSignEventsInterval) {
            clearInterval(this.notSignEventsInterval);
            this.notSignEventsInterval = null;
        }
    }
}